<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Boards') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('boards')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        :aria-label="$t('Reload')"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        type="button"
                        @click="loadBoards"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full relative flex flex-col min-h-0">
                <loading :status="loading"/>
                <div class="board-header-card bg-white shadow">
                    <div class="board-header-toolbar relative overflow-visible border-b border-ultralight">
                        <div class="board-header-top-row flex min-h-[3.625rem] items-stretch justify-between">
                            <div class="flex min-w-0 flex-1 items-stretch">
                                <div v-on-clickaway="closeBoardFilterMenu" class="board-header-filter-cell relative flex flex-shrink-0 border-r border-ultralight">
                                    <button
                                        :aria-label="$t('Filter')"
                                        class="board-header-icon-cell"
                                        :title="$t('Filter')"
                                        type="button"
                                        @click="toggleBoardFilterMenu"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                                    </button>
                                    <div
                                        v-show="boardFilterMenuOpen"
                                        class="board-filter-menu absolute left-0 top-full mt-2 w-max min-w-[12rem] max-w-[20rem] rounded border border-bordergray-100 bg-white shadow-lg"
                                    >
                                        <div class="py-1 flex flex-col">
                                            <button
                                                v-for="option in boardFilterOptions"
                                                :key="'board-filter-option-' + option.value"
                                                :class="boardFilter === option.value ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'"
                                                class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm hover:bg-gray-100"
                                                type="button"
                                                @click="setBoardFilter(option.value)"
                                            >
                                                {{ option.label }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div v-on-clickaway="closeBoardSelectMenu" class="board-select-cell relative flex min-w-0 flex-shrink-0 items-center border-r border-ultralight">
                                    <div class="relative flex h-full min-w-0 items-center">
                                        <button
                                            class="board-select-trigger flex h-full min-w-[18rem] max-w-[26rem] items-center bg-white py-0 pl-5 pr-10 text-left text-sm font-medium leading-5 text-gray-800 focus:outline-none"
                                            :aria-expanded="boardSelectMenuOpen ? 'true' : 'false'"
                                            :aria-label="$t('Boards')"
                                            type="button"
                                            @click="toggleBoardSelectMenu"
                                        >
                                            <img
                                                v-if="activeBoard && getBoardProject(activeBoard)"
                                                :src="getBoardProjectImageUrl(activeBoard)"
                                                :alt="getBoardProject(activeBoard).title || $t('Project')"
                                                class="mr-3 h-6 w-6 flex-shrink-0 rounded-sm object-cover"
                                            >
                                            <span class="min-w-0 flex-1 truncate" :title="activeBoard ? activeBoard.title : ''">
                                                {{ activeBoard ? activeBoard.title : $t('Boards') }}
                                            </span>
                                            <svg-vue class="absolute right-4 h-[0.875rem] w-[0.875rem] text-gray-900" icon="font-awesome.regular.angle-down"></svg-vue>
                                        </button>
                                        <div
                                            v-show="boardSelectMenuOpen"
                                            class="board-select-menu absolute left-0 top-full mt-2 max-h-80 min-w-full overflow-y-auto rounded border border-bordergray-100 bg-white shadow-lg"
                                        >
                                            <button
                                                v-for="board in boards"
                                                :key="'board-select-menu-option-' + board.id"
                                                :class="activeBoardId === board.id ? 'bg-gray-50 font-semibold text-gray-900' : 'font-normal text-gray-700 hover:bg-gray-50'"
                                                class="flex w-full min-w-0 items-center px-4 py-2 text-left text-sm"
                                                type="button"
                                                @click="selectBoardFromMenu(board.id)"
                                            >
                                                <img
                                                    v-if="getBoardProject(board)"
                                                    :src="getBoardProjectImageUrl(board)"
                                                    :alt="getBoardProject(board).title || $t('Project')"
                                                    class="mr-3 h-6 w-6 flex-shrink-0 rounded-sm object-cover"
                                                >
                                                <span class="min-w-0 flex-1 truncate" :title="board.title">{{ board.title }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="canCreateBoards" class="flex-shrink-0 border-l border-ultralight">
                                <button
                                    class="board-header-create-button btn-stage-primary h-full w-32 cursor-pointer border-0"
                                    type="button"
                                    :title="$t('New board')"
                                    :aria-label="$t('New board')"
                                    @click="openBoardDialog('create')"
                                >
                                    {{ $t('New board') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <template v-if="activeBoard">
                        <div class="rounded-b-sm bg-white px-5">
                            <div class="flex min-h-[3.625rem] flex-wrap items-center justify-between gap-3">
                                <div class="flex min-w-0 items-center">
                                    <div v-if="getBoardApprovalUsers(activeBoard).length" class="mr-3 flex flex-shrink-0 items-center">
                                        <img
                                            v-for="(user, index) in getVisibleBoardApprovalUsers(activeBoard)"
                                            :key="'board-approval-user-' + activeBoard.id + '-' + user.id + '-' + index"
                                            :src="getApprovalUserAvatar(user)"
                                            :alt="user.name || $t('User')"
                                            :title="user.name || ''"
                                            :class="index === 0 ? '' : '-ml-2.5'"
                                            class="inline-block h-6 w-6 rounded-full border border-white object-cover"
                                        >
                                        <span
                                            v-if="getRemainingBoardApprovalUsersCount(activeBoard) > 0"
                                            :title="getRemainingBoardApprovalUsersCount(activeBoard) + ' ' + $t('additional users')"
                                            :class="getVisibleBoardApprovalUsers(activeBoard).length > 0 ? '-ml-2.5' : ''"
                                            class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-200 text-xs font-semibold text-gray-500"
                                        >
                                            +
                                        </span>
                                    </div>
                                    <h2 class="min-w-0 truncate text-base font-semibold leading-6 text-gray-900" :title="activeBoard.title">{{ activeBoard.title }}</h2>
                                    <span
                                        v-if="getBoardCreator(activeBoard)"
                                        class="ml-2 max-w-[12rem] flex-shrink-0 truncate text-sm font-normal leading-5 text-gray-400"
                                        :title="$t('Created by') + ' ' + getBoardCreator(activeBoard).name"
                                    >
                                        {{ $t('by') }} {{ getBoardCreator(activeBoard).name }}
                                    </span>
                                </div>
                                <div v-if="activeBoardCanManage" class="inline-flex flex-shrink-0 items-center gap-1">
                                    <button v-if="activeBoard.canShare" class="btn-stage-icon-plain" type="button" :title="$t('Approval management')" :aria-label="$t('Approval management')" @click="openApprovalDialog">
                                        <svg-vue class="h-4 w-5" icon="font-awesome.regular.user-lock"></svg-vue>
                                    </button>
                                    <button class="btn-stage-icon-plain ml-1" type="button" :title="$t('Add column')" :aria-label="$t('Add column')" @click="openColumnDialog('create')">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                                    </button>
                                    <div v-if="activeBoardCanManage" v-on-clickaway="closeBoardActionsMenu" class="relative inline-flex items-center">
                                        <button class="btn-stage-icon-plain" type="button" :title="$t('Options')" :aria-label="$t('Options')" @click="toggleBoardActionsMenu">
                                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                        </button>
                                        <div
                                            v-show="boardActionsOpen"
                                            class="origin-top-right absolute top-full right-0 mt-2 w-max min-w-[10rem] max-w-[18rem] rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                        >
                                            <div class="py-1 flex flex-col">
                                                <button
                                                class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    type="button"
                                                    @click="boardActionsOpen = false; openBoardDialog('rename', activeBoard)"
                                                >
                                                    {{ $t('Edit board') }}
                                                </button>
                                                <button
                                                    v-if="activeBoard.canArchive"
                                                    class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    type="button"
                                                    @click="boardActionsOpen = false; toggleArchiveActiveBoard()"
                                                >
                                                    {{ activeBoard.archived ? $t('Unarchive board') : $t('Archive board') }}
                                                </button>
                                                <button
                                                    v-if="activeBoard.canDelete"
                                                class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    type="button"
                                                    @click="boardActionsOpen = false; requestRemoveActiveBoard()"
                                                >
                                                    {{ $t('Delete board') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-else
                                    class="inline-flex flex-shrink-0 items-center gap-2 text-sm font-normal leading-5 text-gray-400"
                                    role="status"
                                    :title="$t('Schreibgeschützt')"
                                >
                                    <svg-vue class="h-5 w-3" icon="font-awesome.regular.lock"></svg-vue>
                                    <span>{{ $t('Schreibgeschützt') }}</span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <template v-if="activeBoard">
                    <div class="relative mt-6 -ml-8 -mr-8 flex-1 min-h-0 overflow-hidden sm:-ml-8 sm:-mr-8 lg:-ml-8 lg:-mr-8">
                        <div class="board-scroll h-full overflow-x-auto overflow-y-visible pt-3 pb-1">
                            <div
                                v-if="activeBoard.columns.length < 1"
                                class="flex h-full min-w-full items-center justify-center px-8"
                            >
                                <empty-state :title="$t('No columns created')" />
                            </div>
                            <div
                                v-else
                                data-board-columns-track
                                class="flex h-[calc(100%-0.25rem)] min-w-full items-start gap-4 pl-8 sm:pl-8 lg:pl-8"
                                @dragover.prevent="moveColumnToPointer($event)"
                                @drop.prevent="finishColumnDrag"
                            >
                                <section
                                    v-for="column in activeBoard.columns"
                                    :key="'board-column-' + column.id"
                                    :class="{
                                        'opacity-70 shadow-lg ring-2 ring-gray-300': draggingColumnId === column.id,
                                        'ring-2 ring-gray-300 ring-offset-2 ring-offset-stage-background': draggingColumnOverId === column.id && draggingColumnId !== column.id,
                                        'board-column-readonly': !activeBoardCanManage,
                                    }"
                                    :data-board-column-id="column.id"
                                    class="board-column-item relative flex h-full w-80 flex-shrink-0 flex-col rounded-sm bg-white shadow"
                                    @dragover.prevent.stop="moveColumnOver(column.id, $event)"
                                    @drop.stop.prevent="finishColumnDrag"
                                >
                                    <span
                                        v-if="getColumnColor(column)"
                                        class="absolute inset-y-0 left-0 w-[6px] rounded-l-sm"
                                        :style="getColorPresetSwatchStyle(getColumnColor(column))"
                                    ></span>
                                    <button
                                        :aria-label="$t('Move column')"
                                        :class="activeBoardCanManage ? 'board-drag-surface' : 'cursor-default'"
                                        class="absolute left-[20px] top-[-11px] z-20 flex h-[11px] w-12 items-center justify-center gap-1 rounded-t-lg border border-b-0 border-bordergray-100 bg-white text-gray-400"
                                        :draggable="activeBoardCanManage"
                                        :title="$t('Move column')"
                                        type="button"
                                        @dragstart.stop="startColumnDrag(column.id, $event)"
                                        @dragend.stop="finishColumnDrag"
                                    >
                                        <span class="relative top-[3px] h-[3px] w-[3px] rounded-full bg-current"></span>
                                        <span class="relative top-[3px] h-[3px] w-[3px] rounded-full bg-current"></span>
                                        <span class="relative top-[3px] h-[3px] w-[3px] rounded-full bg-current"></span>
                                    </button>
                                    <div class="flex min-h-[3.5rem] items-center border-b border-ultralight py-3 pl-5 pr-2" @dblclick.stop="openColumnDialogFromDoubleClick(column, $event)">
                                        <div class="flex min-w-0 flex-1 items-center justify-between gap-2">
                                            <div class="min-w-0 flex-1">
                                                <h3 class="block truncate text-[15px] font-semibold leading-5 text-gray-700" :title="column.title">{{ column.title }}</h3>
                                            </div>
                                            <div v-if="activeBoardCanManage" v-on-clickaway="closeColumnActionsMenu" class="relative -mr-1 inline-flex flex-shrink-0 items-center self-center">
                                                <button class="btn-stage-icon-plain h-8 w-6 justify-center text-gray-500 hover:text-gray-700" :title="$t('Add card')" :aria-label="$t('Add card')" type="button" @click.stop="openCardDialog(column.id)">
                                                    <svg-vue class="h-3.5 w-3.5" icon="font-awesome.regular.plus"></svg-vue>
                                                </button>
                                                <button class="btn-stage-icon-plain h-8 w-6 justify-center text-gray-500" :title="$t('Options')" :aria-label="$t('Options')" type="button" @click="toggleColumnActionsMenu(column.id)">
                                                    <svg-vue class="h-[0.8125rem] w-[0.8125rem]" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                                <div
                                                    v-show="columnActionsOpenId === column.id"
                                                    class="origin-top-right absolute top-full right-0 mt-1 w-max min-w-[8rem] max-w-[18rem] rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                                >
                                                    <div class="py-1 flex flex-col">
                                                        <button
                                                            class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                            type="button"
                                                            @click="closeColumnActionsMenu(); openColumnDialog('rename', column)"
                                                        >
                                                            {{ $t('Edit column') }}
                                                        </button>
                                                        <button
                                                            v-if="column.canDelete"
                                                            class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                            type="button"
                                                            @click="closeColumnActionsMenu(); requestRemoveColumn(column.id)"
                                                        >
                                                            {{ $t('Delete column') }}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        class="board-column-body min-h-0 flex-1 overflow-y-auto py-3 pl-5 pr-3"
                                        @dragover.prevent.stop="handleColumnBodyDragOver(column.id, $event)"
                                        @drop.stop="dropOnColumnEnd(column.id, $event)"
                                    >
                                        <article
                                            v-for="card in getOpenColumnCards(column)"
                                            :key="'board-card-' + card.id"
                                            :class="getCardItemClass(card)"
                                            class="board-card-item mb-3 rounded-sm border border-bordergray-100 bg-white px-3"
                                            :draggable="activeBoardCanManage"
                                            @dragstart="startCardDrag(column.id, card.id)"
                                            @dragend="endCardDrag"
                                            @dragover.prevent
                                            @dblclick.stop="openCardDialogFromDoubleClick(column.id, card, $event)"
                                            @drop.stop="dropOnCard(column.id, getCardIndexInColumn(column, card.id), $event)"
                                        >
                                            <div :class="card.description ? 'items-start' : 'items-center'" class="flex min-h-8 justify-between gap-2">
                                                <div class="min-w-0">
                                                    <div class="flex min-w-0 items-start gap-2">
                                                        <span
                                                            v-if="card.color"
                                                            :style="getColorPresetSwatchStyle(card.color)"
                                                            class="mt-[0.25rem] h-3 w-3 flex-shrink-0 rounded-full"
                                                            aria-hidden="true"
                                                        ></span>
                                                        <div class="min-w-0 break-words text-sm font-semibold leading-[1.25] text-gray-800">{{ card.title }}</div>
                                                    </div>
                                                    <p v-if="card.description" class="mt-0.5 break-words text-sm leading-[1.35] text-gray-500">{{ truncateCardDescription(card.description) }}</p>
                                                </div>
                                                <div v-if="activeBoardCanManage" v-on-clickaway="closeCardActionsMenu" class="relative -mr-2 inline-flex flex-shrink-0 items-start">
                                                    <button class="btn-stage-icon-plain h-7 w-5 cursor-pointer justify-center text-gray-500 hover:text-gray-700" :title="$t('Options')" :aria-label="$t('Options')" type="button" @click.stop="toggleCardActionsMenu(card.id)">
                                                        <svg-vue class="h-[0.8125rem] w-[0.8125rem]" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                    </button>
                                                    <div
                                                        v-show="cardActionsOpenId === card.id"
                                                        class="origin-top-right absolute top-full right-0 mt-1 w-max min-w-[8rem] max-w-[18rem] rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                                        @click.stop
                                                    >
                                                        <div class="py-1 flex flex-col">
                                                            <button
                                                                class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                type="button"
                                                                @click="closeCardActionsMenu(); openCardDialog(column.id, card)"
                                                            >
                                                                {{ $t('Edit card') }}
                                                            </button>
                                                            <button
                                                                class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                type="button"
                                                                @click="closeCardActionsMenu(); toggleCardCompletion(column.id, card.id)"
                                                            >
                                                                {{ card.completed ? $t('Mark as open') : $t('Mark as completed') }}
                                                            </button>
                                                            <button
                                                                class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                type="button"
                                                                @click="closeCardActionsMenu(); requestRemoveCard(column.id, card.id)"
                                                            >
                                                                {{ $t('Delete card') }}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="getChecklistItems(card).length" class="-mx-3 mt-2 border-t border-ultralight px-3 pt-2">
                                                <div class="mb-1.5 flex items-center justify-between gap-2 text-xs font-normal leading-4 text-gray-400">
                                                    <span>{{ $t('Checklist') }}</span>
                                                    <span>{{ getChecklistProgress(card) }}</span>
                                                </div>
                                                <div class="space-y-1">
                                                    <label
                                                        v-for="item in getVisibleChecklistItems(card)"
                                                        :key="'board-card-checklist-' + item.id"
                                                        class="flex cursor-pointer items-start gap-2 text-xs leading-4 text-gray-600"
                                                        @mousedown.stop
                                                        @click.stop
                                                    >
                                                        <input
                                                            :checked="item.done"
                                                            class="form-checkbox mt-[1px] h-3.5 w-3.5 flex-shrink-0 rounded-sm border-bordergray-100 text-gray-900"
                                                            type="checkbox"
                                                            :disabled="!activeBoardCanManage"
                                                            @change="toggleChecklistItem(column.id, card.id, item.id)"
                                                        >
                                                        <span
                                                            :class="item.done ? 'text-gray-400 line-through' : 'text-gray-600'"
                                                            class="min-w-0 break-words"
                                                        >
                                                            {{ item.title }}
                                                        </span>
                                                    </label>
                                                </div>
                                                <button
                                                    v-if="hasChecklistToggle(card)"
                                                    class="mt-1.5 text-xs font-normal leading-4 text-gray-400 hover:text-gray-700 focus:outline-none"
                                                    type="button"
                                                    @mousedown.stop
                                                    @click.stop="toggleChecklistExpanded(card.id)"
                                                >
                                                    {{ isChecklistExpanded(card.id) ? $t('Show less') : getHiddenChecklistCount(card) + ' ' + $t('more to show') }}
                                                </button>
                                            </div>
                                            <div
                                                v-if="hasCardFooter(card)"
                                                class="board-card-footer -mx-3 mt-2 flex items-center justify-between gap-2 border-t border-ultralight px-3 py-2"
                                            >
                                                <div v-if="getCardAssignees(card).length || card.attachmentCount" class="flex min-w-0 items-center gap-2">
                                                    <div v-if="getCardAssignees(card).length" class="flex min-w-0 items-center">
                                                        <img
                                                            v-for="(assignee, assigneeIndex) in getVisibleCardAssignees(card)"
                                                            :key="'board-card-assignee-avatar-' + card.id + '-' + assignee.id"
                                                            :src="assignee.avatarUrl"
                                                            :alt="assignee.name"
                                                            :title="assignee.name"
                                                            :class="assigneeIndex === 0 ? '' : '-ml-2'"
                                                            class="h-5 w-5 flex-shrink-0 rounded-full border border-white object-cover"
                                                        >
                                                        <span
                                                            v-if="getRemainingCardAssigneeCount(card) > 0"
                                                            :title="getRemainingCardAssigneeCount(card) + ' ' + $t('additional users')"
                                                            class="-ml-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-white bg-gray-200 text-[10px] font-semibold leading-none text-gray-500"
                                                        >
                                                            +
                                                        </span>
                                                    </div>
                                                    <span v-if="card.attachmentCount" class="inline-flex items-center gap-1 text-xs leading-4 text-gray-400" :title="$t('Files')">
                                                        <svg-vue class="h-3.5 w-3.5" icon="font-awesome.regular.paperclip"></svg-vue>
                                                        <span>{{ card.attachmentCount }}</span>
                                                    </span>
                                                </div>
                                                <div v-if="card.dueDate || card.completedAt" class="ml-auto flex flex-shrink-0 flex-col items-end gap-1">
                                                    <div v-if="card.dueDate" class="flex items-center gap-1.5">
                                                        <span :class="isCardOverdue(card) ? 'text-[#d15f5f]' : 'text-gray-500'" class="text-xs leading-4">{{ formatCardDate(card.dueDate) }}</span>
                                                        <span
                                                            v-if="isCardOverdue(card)"
                                                            class="board-card-overdue-badge"
                                                            :title="$t('Overdue')"
                                                        >
                                                            <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <path d="M12 5a7 7 0 0 1 7 7v3.5l1.8 2.1a1 1 0 0 1-.76 1.65H4.96a1 1 0 0 1-.76-1.65L6 15.5V12a7 7 0 0 1 6-6.92V5zm0 16a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 21z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                                                            </svg>
                                                        </span>
                                                    </div>
                                                    <span v-if="card.completedAt" class="flex items-center gap-1.5 text-xs leading-4 text-[#16a34a]">
                                                        <span>{{ formatCardDate(card.completedAt) }}</span>
                                                        <span class="board-card-completed-badge" aria-hidden="true">
                                                            <svg class="h-3 w-3" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M4.1 8.3 6.6 10.8 11.9 5.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                                                            </svg>
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </article>

                                        <div
                                            v-if="getOpenColumnCards(column).length < 1"
                                            class="mb-3 rounded-sm border border-dashed border-bordergray-100 px-3 py-6 text-center text-sm leading-5 text-gray-400"
                                        >
                                            {{ $t('Drop cards here') }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="getCompletedColumnCards(column).length"
                                        :class="[
                                            isCompletedColumnExpanded(column.id) ? 'board-column-completed-footer-open' : '',
                                            getColumnColor(column) ? 'ml-[6px]' : '',
                                        ]"
                                        class="board-column-completed-footer overflow-hidden border-t border-ultralight"
                                    >
                                        <div
                                            class="board-column-completed-scroll"
                                            :class="isCompletedColumnExpanded(column.id) ? 'board-column-completed-scroll-open overflow-y-auto' : ''"
                                        >
                                            <button
                                                class="board-column-completed-trigger flex min-h-[2.75rem] w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm font-normal text-gray-400 focus:outline-none"
                                                type="button"
                                                @click="toggleCompletedColumn(column.id)"
                                            >
                                                <span class="min-w-0 truncate">{{ getCompletedColumnCards(column).length }} {{ $t('completed') }}</span>
                                                <span class="inline-flex flex-shrink-0 items-center gap-2 text-xs font-normal text-gray-400">
                                                    <svg-vue :class="isCompletedColumnExpanded(column.id) ? '-rotate-90' : 'rotate-90'" class="h-3 w-3 text-gray-400 transition-transform" icon="font-awesome.regular.angle-right"></svg-vue>
                                                </span>
                                            </button>
                                            <div v-show="isCompletedColumnExpanded(column.id)" class="board-column-completed-panel px-4 pb-4 pt-1">
                                            <article
                                                v-for="card in getCompletedColumnCards(column)"
                                                :key="'board-card-completed-' + card.id"
                                                :class="getCardItemClass(card)"
                                                class="board-card-item mb-3 rounded-sm border border-bordergray-100 bg-white px-3"
                                                :draggable="activeBoardCanManage"
                                                @dragstart="startCardDrag(column.id, card.id)"
                                                @dragend="endCardDrag"
                                                @dragover.prevent
                                                @dblclick.stop="openCardDialogFromDoubleClick(column.id, card, $event)"
                                                @drop.stop="dropOnCard(column.id, getCardIndexInColumn(column, card.id), $event)"
                                            >
                                                <div :class="card.description ? 'items-start' : 'items-center'" class="flex min-h-8 justify-between gap-2">
                                                    <div class="min-w-0">
                                                        <div class="flex min-w-0 items-start gap-2">
                                                            <span
                                                                v-if="card.color"
                                                                :style="getColorPresetSwatchStyle(card.color)"
                                                                class="mt-[0.25rem] h-3 w-3 flex-shrink-0 rounded-full"
                                                                aria-hidden="true"
                                                            ></span>
                                                            <div class="min-w-0 break-words text-sm font-semibold leading-[1.25] text-gray-800">{{ card.title }}</div>
                                                        </div>
                                                        <p v-if="card.description" class="mt-0.5 break-words text-sm leading-[1.35] text-gray-500">{{ truncateCardDescription(card.description) }}</p>
                                                    </div>
                                                    <div v-if="activeBoardCanManage" v-on-clickaway="closeCardActionsMenu" class="relative -mr-2 inline-flex flex-shrink-0 items-start">
                                                        <button class="btn-stage-icon-plain h-7 w-5 cursor-pointer justify-center text-gray-500 hover:text-gray-700" :title="$t('Options')" :aria-label="$t('Options')" type="button" @click.stop="toggleCardActionsMenu(card.id, $event, true)">
                                                            <svg-vue class="h-[0.8125rem] w-[0.8125rem]" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                        </button>
                                                        <div
                                                            v-show="cardActionsOpenId === card.id"
                                                            :class="cardActionsMenuFixed ? 'fixed' : 'absolute top-full right-0 mt-1'"
                                                            :style="cardActionsMenuFixed ? cardActionsMenuStyle : null"
                                                            class="w-max min-w-[8rem] max-w-[18rem] rounded border border-bordergray-100 bg-white shadow-lg z-[140]"
                                                            @click.stop
                                                        >
                                                            <div class="py-1 flex flex-col">
                                                                <button
                                                                    class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                    type="button"
                                                                    @click="closeCardActionsMenu(); openCardDialog(column.id, card)"
                                                                >
                                                                    {{ $t('Edit card') }}
                                                                </button>
                                                                <button
                                                                    class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                    type="button"
                                                                    @click="closeCardActionsMenu(); toggleCardCompletion(column.id, card.id)"
                                                                >
                                                                    {{ card.completed ? $t('Mark as open') : $t('Mark as completed') }}
                                                                </button>
                                                                <button
                                                                    class="block w-full whitespace-nowrap text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                    type="button"
                                                                    @click="closeCardActionsMenu(); requestRemoveCard(column.id, card.id)"
                                                                >
                                                                    {{ $t('Delete card') }}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-if="getChecklistItems(card).length" class="-mx-3 mt-2 border-t border-ultralight px-3 pt-2">
                                                    <div class="mb-1.5 flex items-center justify-between gap-2 text-xs font-normal leading-4 text-gray-400">
                                                        <span>{{ $t('Checklist') }}</span>
                                                        <span>{{ getChecklistProgress(card) }}</span>
                                                    </div>
                                                    <div class="space-y-1">
                                                        <label
                                                            v-for="item in getVisibleChecklistItems(card)"
                                                            :key="'board-card-completed-checklist-' + item.id"
                                                            class="flex cursor-pointer items-start gap-2 text-xs leading-4 text-gray-600"
                                                            @mousedown.stop
                                                            @click.stop
                                                        >
                                                            <input
                                                                :checked="item.done"
                                                                class="form-checkbox mt-[1px] h-3.5 w-3.5 flex-shrink-0 rounded-sm border-bordergray-100 text-gray-900"
                                                                type="checkbox"
                                                                :disabled="!activeBoardCanManage"
                                                                @change="toggleChecklistItem(column.id, card.id, item.id)"
                                                            >
                                                            <span
                                                                :class="item.done ? 'text-gray-400 line-through' : 'text-gray-600'"
                                                                class="min-w-0 break-words"
                                                            >
                                                                {{ item.title }}
                                                            </span>
                                                        </label>
                                                    </div>
                                                    <button
                                                        v-if="hasChecklistToggle(card)"
                                                        class="mt-1.5 text-xs font-normal leading-4 text-gray-400 hover:text-gray-700 focus:outline-none"
                                                        type="button"
                                                        @mousedown.stop
                                                        @click.stop="toggleChecklistExpanded(card.id)"
                                                    >
                                                        {{ isChecklistExpanded(card.id) ? $t('Show less') : getHiddenChecklistCount(card) + ' ' + $t('more to show') }}
                                                    </button>
                                                </div>
                                                <div
                                                    v-if="hasCardFooter(card)"
                                                    class="board-card-footer -mx-3 mt-2 flex items-center justify-between gap-2 border-t border-ultralight px-3 py-2"
                                                >
                                                    <div v-if="getCardAssignees(card).length || card.attachmentCount" class="flex min-w-0 items-center gap-2">
                                                        <div v-if="getCardAssignees(card).length" class="flex min-w-0 items-center">
                                                            <img
                                                                v-for="(assignee, assigneeIndex) in getVisibleCardAssignees(card)"
                                                                :key="'board-card-completed-assignee-avatar-' + card.id + '-' + assignee.id"
                                                                :src="assignee.avatarUrl"
                                                                :alt="assignee.name"
                                                                :title="assignee.name"
                                                                :class="assigneeIndex === 0 ? '' : '-ml-2'"
                                                                class="h-5 w-5 flex-shrink-0 rounded-full border border-white object-cover"
                                                            >
                                                            <span
                                                                v-if="getRemainingCardAssigneeCount(card) > 0"
                                                                :title="getRemainingCardAssigneeCount(card) + ' ' + $t('additional users')"
                                                                class="-ml-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-white bg-gray-200 text-[10px] font-semibold leading-none text-gray-500"
                                                            >
                                                                +
                                                            </span>
                                                        </div>
                                                        <span v-if="card.attachmentCount" class="inline-flex items-center gap-1 text-xs leading-4 text-gray-400" :title="$t('Files')">
                                                            <svg-vue class="h-3.5 w-3.5" icon="font-awesome.regular.paperclip"></svg-vue>
                                                            <span>{{ card.attachmentCount }}</span>
                                                        </span>
                                                    </div>
                                                    <div v-if="card.dueDate || card.completedAt" class="ml-auto flex flex-shrink-0 flex-col items-end gap-1">
                                                        <div v-if="card.dueDate" class="flex items-center gap-1.5">
                                                            <span :class="isCardOverdue(card) ? 'text-[#d15f5f]' : 'text-gray-500'" class="text-xs leading-4">{{ formatCardDate(card.dueDate) }}</span>
                                                            <span
                                                                v-if="isCardOverdue(card)"
                                                                class="board-card-overdue-badge"
                                                                :title="$t('Overdue')"
                                                            >
                                                                <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                    <path d="M12 5a7 7 0 0 1 7 7v3.5l1.8 2.1a1 1 0 0 1-.76 1.65H4.96a1 1 0 0 1-.76-1.65L6 15.5V12a7 7 0 0 1 6-6.92V5zm0 16a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 21z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                                                                </svg>
                                                            </span>
                                                        </div>
                                                        <span v-if="card.completedAt" class="flex items-center gap-1.5 text-xs leading-4 text-[#16a34a]">
                                                            <span>{{ formatCardDate(card.completedAt) }}</span>
                                                            <span class="board-card-completed-badge" aria-hidden="true">
                                                                <svg class="h-3 w-3" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M4.1 8.3 6.6 10.8 11.9 5.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                                                                </svg>
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </article>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <div
                                    class="h-full min-w-16 flex-1"
                                    @dragover.prevent="moveColumnToEnd"
                                    @drop.stop.prevent="finishColumnDrag"
                                ></div>
                            </div>
                        </div>
                    </div>
                </template>

                <div v-else class="flex flex-1 items-center justify-center px-6 text-center">
                    <empty-state
                        icon="font-awesome.regular.grid-2-plus"
                        :title="$t('No board exists yet')"
                    />
                </div>
            </div>
        </div>

        <teleport to="body">
            <app-dialog
                :open="boardDialogOpen"
                :title="boardDialogMode === 'rename' ? $t('Edit board').toString() : $t('Create board').toString()"
                size="sm"
                header-variant="sectioned"
                panel-class="!overflow-visible"
                body-class="!overflow-visible p-0"
                @close="closeBoardDialog"
            >
                    <form id="board-dialog-form" class="px-6 py-5" novalidate @submit.prevent="saveBoardDialog">
                            <label class="stage-filter-label block" for="board-title">{{ $t('Title') }}</label>
                            <input
                                id="board-title"
                                ref="boardDialogInput"
                                v-model.trim="boardDialogTitle"
                                class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                required
                                type="text"
                            >
                            <label class="stage-filter-label mt-4 block" for="board-project-button">{{ $t('Project') }}</label>
                            <div v-on-clickaway="closeBoardProjectDropdown" class="relative">
                                <button
                                    id="board-project-button"
                                    class="flex h-10 w-full items-center rounded-sm border border-bordergray-100 bg-white px-3 text-left text-sm text-gray-900 shadow-sm transition hover:border-gray-300 focus:outline-none"
                                    type="button"
                                    @click="toggleBoardProjectDropdown"
                                >
                                    <span
                                        v-if="selectedBoardDialogProject"
                                        class="mr-3 inline-flex h-7 w-7 flex-shrink-0 overflow-hidden rounded-sm"
                                    >
                                        <img
                                            :src="selectedBoardDialogProject.imageUrl"
                                            :alt="selectedBoardDialogProject.title || $t('Project')"
                                            class="h-full w-full object-cover"
                                        >
                                    </span>
                                    <span class="min-w-0 flex-1 truncate">
                                        {{ selectedBoardDialogProject ? selectedBoardDialogProject.title : $t('No project') }}
                                    </span>
                                    <svg-vue class="ml-3 h-3.5 w-3.5 flex-shrink-0 rotate-90 text-gray-400" icon="font-awesome.regular.angle-right"></svg-vue>
                                </button>
                                <div
                                    v-show="boardProjectDropdownOpen"
                                    class="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-sm border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                                >
                                    <button
                                        :class="!boardDialogProjectId ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50'"
                                        class="flex w-full items-center px-3 py-2 text-left text-sm"
                                        type="button"
                                        @click="selectBoardProject(null)"
                                    >
                                        <span class="min-w-0 flex-1 truncate">{{ $t('No project') }}</span>
                                    </button>
                                    <button
                                        v-for="project in boardProjects"
                                        :key="'board-project-option-' + project.id"
                                        :class="Number(boardDialogProjectId) === Number(project.id) ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50'"
                                        class="flex w-full items-center px-3 py-2 text-left text-sm"
                                        type="button"
                                        @click="selectBoardProject(project)"
                                    >
                                        <img
                                            :src="project.imageUrl"
                                            :alt="project.title || $t('Project')"
                                            class="mr-3 h-7 w-7 flex-shrink-0 rounded-sm object-cover"
                                        >
                                        <span class="min-w-0 flex-1 truncate">{{ project.title }}</span>
                                    </button>
                                </div>
                            </div>
                    </form>
                <template #footer>
                    <app-dialog-action form="board-dialog-form" native-type="submit" :label="$t('Save').toString()" />
                </template>
            </app-dialog>

            <app-dialog
                :open="columnDialogOpen"
                :title="columnDialogMode === 'rename' ? $t('Edit column').toString() : $t('Create column').toString()"
                size="sm"
                header-variant="sectioned"
                body-class="p-0"
                @close="closeColumnDialog"
            >
                    <form id="column-dialog-form" class="px-6 py-5" novalidate @submit.prevent="saveColumnDialog">
                            <label class="stage-filter-label block" for="board-column-title">{{ $t('Title') }}</label>
                            <input
                                id="board-column-title"
                                ref="columnDialogInput"
                                v-model.trim="columnDialogTitle"
                                class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                required
                                type="text"
                            >
                            <div class="mt-4">
                                <div class="stage-filter-label block">{{ $t('Color') }}</div>
                                <div class="board-color-preset-grid mt-2">
                                    <button
                                        :data-selected="!normalizePresetColor(columnDialogColor) ? 'true' : 'false'"
                                        :title="$t('No selection')"
                                        :aria-label="$t('No selection')"
                                        :aria-pressed="!normalizePresetColor(columnDialogColor) ? 'true' : 'false'"
                                        class="board-color-preset board-color-preset-empty"
                                        type="button"
                                        @click.prevent.stop="selectColumnDialogColor('')"
                                    >
                                        <span class="board-color-preset-swatch board-color-preset-empty-swatch"></span>
                                    </button>
                                    <button
                                        v-for="color in columnColorPresets"
                                        :key="'board-column-color-preset-' + color"
                                        :data-selected="isColorPresetSelected(columnDialogColor, color) ? 'true' : 'false'"
                                        class="board-color-preset"
                                        :aria-label="color"
                                        :aria-pressed="isColorPresetSelected(columnDialogColor, color) ? 'true' : 'false'"
                                        type="button"
                                        @click.prevent.stop="selectColumnDialogColor(color)"
                                    >
                                        <span class="board-color-preset-swatch" :style="getColorPresetSwatchStyle(color)"></span>
                                    </button>
                                </div>
                            </div>
                    </form>
                <template #footer>
                    <app-dialog-action form="column-dialog-form" native-type="submit" :label="$t('Save').toString()" />
                </template>
            </app-dialog>

            <app-dialog
                :open="cardDialogOpen"
                :title="cardDialogMode === 'edit' ? $t('Edit card').toString() : $t('Create card').toString()"
                size="sm"
                header-variant="sectioned"
                panel-class="board-card-dialog-panel"
                body-class="p-0"
                :busy="cardDialogSaving"
                @close="closeCardDialog"
            >
                    <form id="card-dialog-form" class="board-card-dialog-scroll min-h-0 overflow-y-auto px-6 pb-5 pt-4" novalidate @submit.prevent="saveCardDialog">
                            <label class="stage-filter-label block" for="board-card-title">{{ $t('Title') }}</label>
                            <input
                                id="board-card-title"
                                ref="cardDialogInput"
                                v-model.trim="cardDialogTitle"
                                class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                required
                                type="text"
                            >
                            <label class="stage-filter-label mt-4 block" for="board-card-description">{{ $t('Description') }}</label>
                            <textarea
                                id="board-card-description"
                                v-model.trim="cardDialogDescription"
                                class="stage-filter-input form-textarea block min-h-[10rem] w-full text-sm sm:leading-5"
                                rows="7"
                            ></textarea>
                            <div class="mt-4">
                                <div class="stage-filter-label block">{{ $t('Color') }}</div>
                                <div class="board-color-preset-grid mt-2">
                                    <button
                                        :data-selected="!normalizePresetColor(cardDialogColor) ? 'true' : 'false'"
                                        :title="$t('No selection')"
                                        :aria-label="$t('No selection')"
                                        :aria-pressed="!normalizePresetColor(cardDialogColor) ? 'true' : 'false'"
                                        class="board-color-preset board-color-preset-empty"
                                        type="button"
                                        @click.prevent.stop="selectCardDialogColor('')"
                                    >
                                        <span class="board-color-preset-swatch board-color-preset-empty-swatch"></span>
                                    </button>
                                    <button
                                        v-for="color in cardColorPresets"
                                        :key="'board-card-color-preset-' + color"
                                        :data-selected="isColorPresetSelected(cardDialogColor, color) ? 'true' : 'false'"
                                        class="board-color-preset"
                                        :aria-label="color"
                                        :aria-pressed="isColorPresetSelected(cardDialogColor, color) ? 'true' : 'false'"
                                        type="button"
                                        @click.prevent.stop="selectCardDialogColor(color)"
                                    >
                                        <span class="board-color-preset-swatch" :style="getColorPresetSwatchStyle(color)"></span>
                                    </button>
                                </div>
                            </div>
                            <label class="stage-filter-label mt-4 block" for="board-card-assignee">{{ $t('Responsibilities') }}</label>
                            <div id="board-card-assignee" class="max-h-44 overflow-y-auto rounded-sm border border-bordergray-100 bg-white">
                                <label
                                    v-for="assignee in getCardDialogAssignableUsers()"
                                    :key="'board-card-assignee-option-' + assignee.id"
                                    class="flex cursor-pointer items-center border-b border-ultralight px-3 py-3 last:border-b-0"
                                >
                                    <input
                                        v-model="cardDialogAssigneeIds"
                                        :value="assignee.id"
                                        class="form-checkbox mr-4 h-4 w-4 text-gray-600 transition duration-150 ease-in-out"
                                        type="checkbox"
                                    >
                                    <img
                                        :alt="assignee.name || $t('Avatar')"
                                        :src="getUserAvatar(assignee)"
                                        class="h-6 w-6 flex-shrink-0 rounded-full object-cover mr-4"
                                    >
                                    <span class="block min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{{ assignee.name }}</span>
                                </label>
                                <div v-if="getCardDialogAssignableUsers().length < 1" class="px-3 py-4 text-sm text-gray-500">
                                    {{ $t('No users available') }}
                                </div>
                            </div>
                            <div class="mt-4">
                                <div class="flex items-center justify-between gap-2">
                                    <label class="board-card-checklist-label stage-filter-label block" for="board-card-checklist-new">{{ $t('Checklist') }}</label>
                                    <span v-if="cardDialogChecklist.length" class="text-xs leading-4 text-gray-400">{{ getDialogChecklistProgress() }}</span>
                                </div>
                                <div>
                                    <div v-if="cardDialogChecklist.length" class="board-dialog-checklist-scroll max-h-[182px] overflow-y-auto rounded-sm border border-bordergray-100 bg-white">
                                        <div class="divide-y divide-ultralight">
                                            <div
                                                v-for="(item, itemIndex) in cardDialogChecklist"
                                                :key="'board-card-dialog-checklist-' + item.id"
                                                class="flex items-center gap-2 px-2 py-2.5"
                                            >
                                                <input
                                                    v-model="item.done"
                                                    class="form-checkbox h-4 w-4 flex-shrink-0 rounded-sm border-bordergray-100 text-gray-900"
                                                    type="checkbox"
                                                >
                                                <div class="flex h-10 min-w-0 flex-1 overflow-hidden rounded-sm border border-bordergray-100 bg-white">
                                                    <input
                                                        v-model.trim="item.title"
                                                        class="board-checklist-text-input block h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-gray-900 focus:outline-none"
                                                        type="text"
                                                        @keydown.enter.prevent="focusChecklistInputOrAdd(itemIndex)"
                                                    >
                                                    <button
                                                        class="flex w-10 flex-shrink-0 items-center justify-center text-gray-400 hover:text-gray-500 focus:outline-none"
                                                        :title="$t('Remove item')"
                                                        type="button"
                                                        @click="removeChecklistDialogItem(item.id)"
                                                    >
                                                        <svg-vue class="h-3.5 w-3.5 text-gray-400" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-1 flex h-10 w-full overflow-hidden rounded-sm border border-bordergray-100 bg-white">
                                        <input
                                            id="board-card-checklist-new"
                                            ref="cardDialogChecklistInput"
                                            v-model="cardDialogChecklistTitle"
                                            class="board-checklist-text-input block h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                                            :placeholder="$t('New checklist item')"
                                            type="text"
                                            @keydown.enter.prevent.stop="addChecklistDialogItem"
                                        >
                                        <button class="flex w-10 flex-shrink-0 items-center justify-center text-gray-900 hover:text-gray-900 focus:outline-none" :title="$t('Add item')" type="button" @click.prevent.stop="addChecklistDialogItem">
                                            <svg-vue class="h-4 w-4 text-gray-900" icon="font-awesome.regular.plus"></svg-vue>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-4">
                                <div class="flex items-center justify-between gap-3">
                                    <span class="stage-filter-label mb-0 block">{{ $t('File attachments') }}</span>
                                    <button
                                        class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center text-gray-700 hover:text-gray-900 focus:outline-none disabled:cursor-default disabled:opacity-50"
                                        :disabled="cardDialogSaving || cardDialogUploadBusy || cardDialogAttachmentMutationBusy || cardDialogAttachmentsLoading"
                                        :title="$t('Upload')"
                                        type="button"
                                        @click="triggerCardDialogFileInput"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.arrow-up-from-line"></svg-vue>
                                    </button>
                                    <input
                                        ref="cardDialogFileInput"
                                        class="hidden"
                                        multiple
                                        type="file"
                                        @change="uploadCardDialogFiles"
                                    >
                                </div>
                                <div v-if="cardDialogUploadBusy" class="mt-2 h-1 overflow-hidden rounded-sm bg-gray-200">
                                    <div class="h-full bg-gray-700 transition-all" :style="{width: cardDialogUploadProgress + '%'}"></div>
                                </div>
                                <div v-if="cardDialogAttachmentsLoading" class="mt-2 text-sm text-gray-400">{{ $t('Loading') }}</div>
                                <div v-else-if="cardDialogAttachments.length" class="board-card-attachment-list mt-2 flex w-full flex-col gap-2">
                                    <attachment
                                        v-for="(attachment, attachmentIndex) in cardDialogAttachments"
                                        :key="'board-card-dialog-attachment-' + (attachment.uuid || attachment.id)"
                                        :details="attachment"
                                        @remove="removeCardDialogAttachment(attachmentIndex)"
                                    />
                                </div>
                            </div>
                            <label class="stage-filter-label mt-4 block" for="board-card-due-date">{{ $t('Due date') }}</label>
                            <input
                                id="board-card-due-date"
                                ref="cardDialogDueDateInput"
                                v-model="cardDialogDueDate"
                                class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                type="date"
                            >
                            <label class="mt-4 flex cursor-pointer items-center text-sm font-medium text-gray-700">
                                <input
                                    v-model="cardDialogCompleted"
                                    class="form-checkbox mr-3 h-4 w-4 rounded-sm border-bordergray-100 text-gray-900"
                                    type="checkbox"
                                >
                                <span>{{ $t('Mark as completed') }}</span>
                            </label>
                    </form>
                <template #footer>
                    <app-dialog-action
                        form="card-dialog-form"
                        native-type="submit"
                        :label="$t('Save').toString()"
                        :disabled="cardDialogSaving || cardDialogUploadBusy || cardDialogAttachmentMutationBusy || cardDialogAttachmentsLoading || !cardDialogAttachmentsLoaded"
                        :loading="cardDialogSaving"
                    />
                </template>
            </app-dialog>

            <confirm-dialog
                :open="deleteDialog.open"
                :title="deleteDialog.title"
                :text="deleteDialog.text"
                :confirm-label="$t('Delete')"
                :close-label="$t('Cancel')"
                confirm-button-class="btn btn-red"
                :hide-close-button="true"
                :show-header-close-button="true"
                :sectioned="true"
                headline-id="board-delete-dialog-headline"
                @close="closeDeleteDialog"
                @confirm="confirmDelete"
            />

            <approval-management-dialog
                :open="approvalDialogOpen"
                :title="$t('Approval management')"
                :internal-users="approvalInternalUsers"
                :customer-users="approvalCustomerUsers"
                :guest-users="approvalGuestUsers"
                :selected-internal-ids="activeBoard ? activeBoard.approvalInternalIds : []"
                :selected-customer-ids="activeBoard ? activeBoard.approvalCustomerIds : []"
                :selected-guest-ids="[]"
                :selected-internal-permissions="activeBoard ? activeBoard.approvalInternalPermissions : {}"
                :selected-customer-permissions="activeBoard ? activeBoard.approvalCustomerPermissions : {}"
                :permission-mode="true"
                :read-label="$t('Read')"
                :edit-label="$t('Edit')"
                :internal-tab-label="$t('Internal release')"
                :customer-tab-label="$t('Customer release')"
                :empty-internal-text="$t('No internal users available.')"
                :empty-customer-text="approvalEmptyCustomerText"
                :show-tab-bar="approvalDialogShowTabs"
                :balanced-chrome="true"
                :show-customer-tab="true"
                :show-guest-tab="false"
                :initial-tab="approvalInitialTab"
                @close="closeApprovalDialog"
                @save="saveApprovalDialog"
            />
        </teleport>
    </div>
</template>

<script>
import ApprovalManagementDialog from '@/components/modals/approval-management-dialog';
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';
import ConfirmDialog from '@/components/modals/confirm-dialog';

const DEFAULT_COLUMN_COLOR = '#202830';
const DEFAULT_ASSIGNEE_ID = null;
const CHECKLIST_PREVIEW_LIMIT = 5;
const CARD_DESCRIPTION_PREVIEW_LIMIT = 120;
const BOARD_COLOR_PRESETS = [
    '#202830',
    '#334155',
    '#64748B',
    '#94A3B8',
    '#C81E1E',
    '#E11D48',
    '#F43F5E',
    '#EA580C',
    '#F97316',
    '#F59E0B',
    '#D97706',
    '#65A30D',
    '#16A34A',
    '#059669',
    '#0D9488',
    '#0891B2',
    '#0284C7',
    '#2563EB',
    '#1D4ED8',
    '#4F46E5',
    '#7C3AED',
    '#9333EA',
    '#A21CAF',
    '#C026D3',
    '#DB2777',
    '#BE185D',
    '#9F1239',
    '#0F766E',
    '#1E40AF',
    '#5B21B6',
];
const CARD_COLOR_PRESETS = BOARD_COLOR_PRESETS;
const MAX_CARD_ATTACHMENTS = 20;

export default {
    name: 'workspace-boards',
    components: {
        ApprovalManagementDialog,
        AppDialog,
        AppDialogAction,
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            boards: [],
            activeBoardId: null,
            boardPermissions: {
                canCreateBoards: false,
            },
            approvalDialogOpen: false,
            approvalInternalUsers: [],
            approvalCustomerUsers: [],
            approvalGuestUsers: [],
            approvalDialogScope: 'full',
            approvalDialogShowTabs: true,
            approvalDialogInitialTab: 'internal',
            approvalEmptyCustomerText: '',
            boardFilterAvailability: {
                all: true,
                mine: true,
                shared: false,
                archived: false,
            },
            boardFilter: 'all',
            boardFilterMenuOpen: false,
            boardSelectMenuOpen: false,
            boardActionsOpen: false,
            columnActionsOpenId: null,
            cardActionsOpenId: null,
            cardActionsMenuFixed: false,
            cardActionsMenuStyle: null,
            boardProjects: [],
            boardDialogOpen: false,
            boardDialogMode: 'create',
            boardDialogBoardId: null,
            boardDialogTitle: '',
            boardDialogProjectId: '',
            boardProjectDropdownOpen: false,
            columnDialogOpen: false,
            columnDialogMode: 'create',
            columnDialogColumnId: null,
            columnDialogTitle: '',
            columnDialogColor: DEFAULT_COLUMN_COLOR,
            columnDialogIsProtected: false,
            cardDialogOpen: false,
            cardDialogMode: 'create',
            cardDialogColumnId: null,
            cardDialogCardId: null,
            cardDialogTitle: '',
            cardDialogDescription: '',
            cardDialogColor: '',
            cardDialogAssigneeIds: [],
            cardDialogDueDate: '',
            cardDialogCompleted: false,
            cardDialogChecklist: [],
            cardDialogChecklistTitle: '',
            cardDialogAttachments: [],
            cardDialogDraftAttachmentIds: [],
            cardDialogAttachmentsLoading: false,
            cardDialogAttachmentsLoaded: true,
            cardDialogUploadBusy: false,
            cardDialogAttachmentMutationBusy: false,
            cardDialogSaving: false,
            cardDialogUploadProgress: 0,
            expandedCompletedColumnIds: [],
            expandedChecklistCardIds: [],
            cardAssignees: [],
            deleteDialog: {
                open: false,
                type: null,
                title: '',
                text: '',
                payload: null,
            },
            draggingCard: null,
            draggingColumnId: null,
            draggingColumnOverId: null,
            draggingColumnMoved: false,
            columnColorPresets: BOARD_COLOR_PRESETS,
            cardColorPresets: CARD_COLOR_PRESETS,
        };
    },
    computed: {
        activeBoard() {
            return this.boards.find((board) => board.id === this.activeBoardId) || null;
        },
        canCreateBoards() {
            return !!(this.boardPermissions && this.boardPermissions.canCreateBoards);
        },
        activeBoardCanManage() {
            return !!(this.activeBoard && this.activeBoard.canEdit);
        },
        selectedBoardDialogProject() {
            const selectedProjectId = Number(this.boardDialogProjectId);
            if (selectedProjectId < 1) {
                return null;
            }

            return this.boardProjects.find((project) => Number(project.id) === selectedProjectId) || null;
        },
        approvalInitialTab() {
            if (this.approvalDialogInitialTab === 'customer') {
                return 'customer';
            }
            if (this.approvalInternalUsers.length > 0) {
                return 'internal';
            }
            if (this.approvalCustomerUsers.length > 0) {
                return 'customer';
            }
            if (this.approvalGuestUsers.length > 0) {
                return 'guest';
            }
            return 'internal';
        },
        boardFilterOptions() {
            const options = [
                {value: 'all', label: this.$t('All boards')},
                {value: 'mine', label: this.$t('My boards')},
            ];

            if (this.boardFilterAvailability.shared) {
                options.push({value: 'shared', label: this.$t('Boards shared with me')});
            }

            if (this.boardFilterAvailability.archived) {
                options.push({value: 'archived', label: this.$t('Archived boards')});
            }

            return options;
        },
    },
    mounted() {
        this.loadBoardOptions();
        this.loadBoards();
    },
    methods: {
        loadBoardOptions() {
            axios.get('api/workspace/boards/options')
                .then((response) => this.applyOptions(response.data || {}))
                .catch(() => this.notifyError(this.$t('An error occurred while loading data').toString()));
        },
        loadBoards(options = {}) {
            const preferredActiveBoardId = Number(options.activeBoardId || 0) || null;
            const previousActiveBoardId = preferredActiveBoardId || this.activeBoardId;
            if (options.filter && ['all', 'mine', 'shared', 'archived'].includes(options.filter)) {
                this.boardFilter = options.filter;
            }
            this.loading = true;

            axios.get('api/workspace/boards', {
                params: {
                    filter: this.boardFilter,
                    board_id: previousActiveBoardId || undefined,
                },
            }).then((response) => {
                const responseData = response.data || {};
                this.applyFilterAvailability(responseData.filters || {});

                const boardSummaries = this.normalizeBoards(Array.isArray(responseData.boards) ? responseData.boards : []);
                const activeBoard = responseData.active_board
                    ? this.normalizeBoards([responseData.active_board])[0]
                    : null;

                this.boards = boardSummaries.map((board) => (
                    activeBoard && board.id === activeBoard.id ? activeBoard : board
                ));
                this.applyBoardDeleteAvailability();

                if (this.boards.length < 1 && ['archived', 'shared'].includes(this.boardFilter)) {
                    this.loading = false;
                    this.loadBoards({
                        activeBoardId: previousActiveBoardId,
                        filter: 'mine',
                    });
                    return;
                }

                const activeBoardId = activeBoard ? activeBoard.id : previousActiveBoardId;
                const stillAvailable = this.boards.some((board) => board.id === activeBoardId);
                this.activeBoardId = stillAvailable
                    ? activeBoardId
                    : (this.boards[0] ? this.boards[0].id : null);
                this.loading = false;
            }).catch(() => {
                this.loading = false;
                this.notifyError(this.$t('An error occurred while loading data').toString());
            });
        },
        applyOptions(options) {
            this.boardPermissions = {
                canCreateBoards: !!(options.permissions && options.permissions.can_create_boards),
            };
            this.applyFilterAvailability(options.filters || {});
            this.cardAssignees = this.uniqueUsers([
                ...(Array.isArray(options.assignees) ? options.assignees : []),
                ...(Array.isArray(options.internal) ? options.internal : []),
                ...(Array.isArray(options.customers) ? options.customers : []),
                ...(Array.isArray(options.guests) ? options.guests : []),
            ]);
            this.approvalInternalUsers = Array.isArray(options.internal) ? options.internal : [];
            this.approvalCustomerUsers = Array.isArray(options.customers) ? options.customers : [];
            this.approvalGuestUsers = Array.isArray(options.guests) ? options.guests : [];
            this.boardProjects = this.normalizeProjects(Array.isArray(options.projects) ? options.projects : []);
        },
        applyFilterAvailability(filters) {
            this.boardFilterAvailability = {
                all: true,
                mine: true,
                shared: !!filters.shared,
                archived: !!filters.archived,
            };
        },
        normalizeBoards(boards) {
            return boards.map((board) => {
                const detailsLoaded = Array.isArray(board.columns);

                return {
                    ...board,
                    detailsLoaded,
                    id: Number(board.id),
                    projectId: Number(board.projectId || 0) || null,
                    project: board.project ? this.normalizeProject(board.project) : null,
                    createdById: Number(board.createdById || 0),
                    archived: !!board.archived,
                    archivedAt: board.archivedAt || null,
                    canManage: !!board.canManage,
                    canEdit: board.canEdit !== undefined ? !!board.canEdit : !!board.canManage,
                    canShare: !!board.canShare,
                    canDelete: !!board.canDelete,
                    canArchive: !!board.canArchive,
                    assignableUsers: this.uniqueUsers(Array.isArray(board.assignableUsers) ? board.assignableUsers : []),
                    approvalInternalIds: this.normalizeApprovalIds(board.approvalInternalIds),
                    approvalCustomerIds: this.normalizeApprovalIds(board.approvalCustomerIds),
                    approvalGuestIds: [],
                    approvalUsers: this.uniqueUsers(Array.isArray(board.approvalUsers) ? board.approvalUsers : []),
                    approvalInternalPermissions: this.normalizeApprovalPermissions(board.approvalInternalPermissions, board.approvalInternalIds),
                    approvalCustomerPermissions: this.normalizeApprovalPermissions(board.approvalCustomerPermissions, board.approvalCustomerIds),
                    columns: detailsLoaded
                        ? board.columns.map((column) => ({
                            ...column,
                            id: Number(column.id),
                            color: this.normalizePresetColor(column.color),
                            isProtected: !!column.isProtected,
                            canDelete: column.canDelete !== false && board.columns.length > 1,
                            cards: Array.isArray(column.cards)
                                ? column.cards.map((card) => ({
                                    ...card,
                                    id: Number(card.id),
                                    color: this.normalizeCardColor(card.color),
                                    assigneeId: card.assigneeId || DEFAULT_ASSIGNEE_ID,
                                    assignee: card.assignee || null,
                                    assigneeIds: this.normalizeApprovalIds(Array.isArray(card.assigneeIds) ? card.assigneeIds : (card.assigneeId ? [card.assigneeId] : [])),
                                    assignees: this.uniqueUsers(Array.isArray(card.assignees) ? card.assignees : (card.assignee ? [card.assignee] : [])),
                                    dueDate: card.dueDate || '',
                                    completed: !!card.completed,
                                    completedAt: card.completedAt || '',
                                    attachmentCount: Math.max(0, Number(card.attachmentCount) || 0),
                                    checklist: this.normalizeChecklist(card.checklist),
                                }))
                                : [],
                        }))
                        : [],
                };
            });
        },
        applyBoardDeleteAvailability() {
            this.boards = this.boards.map((board) => ({
                ...board,
                canDelete: !!board.canManage && !!board.canDelete,
            }));
        },
        normalizeApprovalIds(values) {
            return (Array.isArray(values) ? values : [])
                .map((value) => Number(value))
                .filter((value) => value > 0);
        },
        normalizeApprovalPermissions(values, fallbackIds = []) {
            const permissions = {};
            Object.entries(values && typeof values === 'object' ? values : {}).forEach(([userId, permission]) => {
                const normalizedUserId = Number(userId);
                if (normalizedUserId > 0 && ['read', 'edit'].includes(permission)) {
                    permissions[normalizedUserId] = permission;
                }
            });

            if (Object.keys(permissions).length === 0) {
                this.normalizeApprovalIds(fallbackIds).forEach((userId) => {
                    permissions[userId] = 'read';
                });
            }

            return permissions;
        },
        normalizeProjects(projects) {
            return (Array.isArray(projects) ? projects : [])
                .map((project) => this.normalizeProject(project))
                .filter((project) => project && project.id > 0);
        },
        normalizeProject(project) {
            if (!project) {
                return null;
            }

            return {
                ...project,
                id: Number(project.id || 0),
                title: project.title || '',
                imageUrl: project.imageUrl || project.image_url || '/images/default/icon.png',
            };
        },
        normalizeChecklist(checklist) {
            if (!Array.isArray(checklist)) {
                return [];
            }

            return checklist
                .filter((item) => item && item.title)
                .map((item) => ({
                    id: Number(item.id || 0),
                    title: item.title,
                    done: !!item.done,
                }));
        },
        normalizeCardColor(color) {
            const value = String(color || '').trim();
            return /^#[0-9a-fA-F]{6}$/.test(value) ? value.toUpperCase() : '';
        },
        normalizePresetColor(color) {
            const value = String(color || '').trim();
            return /^#[0-9a-fA-F]{6}$/.test(value) ? value.toUpperCase() : '';
        },
        isColorPresetSelected(value, preset) {
            return this.normalizePresetColor(value) === this.normalizePresetColor(preset);
        },
        selectColumnDialogColor(color) {
            this.columnDialogColor = this.normalizePresetColor(color);
        },
        selectCardDialogColor(color) {
            this.cardDialogColor = this.normalizePresetColor(color);
        },
        getColorPresetSwatchStyle(color) {
            const normalizedColor = this.normalizePresetColor(color);

            return {
                background: `linear-gradient(to bottom, ${this.mixHexColor(normalizedColor, '#ffffff', 0.18)} 0%, ${normalizedColor} 54%, ${this.mixHexColor(normalizedColor, '#000000', 0.12)} 100%)`,
            };
        },
        mixHexColor(hexColor, targetColor, amount) {
            const color = this.hexToRgb(hexColor);
            const target = this.hexToRgb(targetColor);

            if (!color || !target) {
                return hexColor;
            }

            const mixed = {
                r: Math.round(color.r + (target.r - color.r) * amount),
                g: Math.round(color.g + (target.g - color.g) * amount),
                b: Math.round(color.b + (target.b - color.b) * amount),
            };

            return this.rgbToHex(mixed);
        },
        hexToRgb(hexColor) {
            const normalizedColor = this.normalizePresetColor(hexColor);

            if (!normalizedColor) {
                return null;
            }

            const value = normalizedColor.slice(1);

            return {
                r: parseInt(value.slice(0, 2), 16),
                g: parseInt(value.slice(2, 4), 16),
                b: parseInt(value.slice(4, 6), 16),
            };
        },
        rgbToHex(color) {
            return `#${[color.r, color.g, color.b].map((value) => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
        },
        getColorPresetButtonClass(selected) {
            return [
                'board-color-preset',
                selected ? 'board-color-preset-selected' : '',
            ];
        },
        truncateCardDescription(description) {
            const value = String(description || '').trim();
            if (value.length <= CARD_DESCRIPTION_PREVIEW_LIMIT) {
                return value;
            }

            return `${value.slice(0, CARD_DESCRIPTION_PREVIEW_LIMIT).trim()}...`;
        },
        cloneChecklist(checklist) {
            return this.normalizeChecklist(checklist).map((item) => ({...item}));
        },
        getChecklistItems(card) {
            return this.normalizeChecklist(card && card.checklist);
        },
        getOpenColumnCards(column) {
            return (Array.isArray(column && column.cards) ? column.cards : []).filter((card) => !card.completed);
        },
        getCompletedColumnCards(column) {
            return (Array.isArray(column && column.cards) ? column.cards : []).filter((card) => card.completed);
        },
        getCardIndexInColumn(column, cardId) {
            return (Array.isArray(column && column.cards) ? column.cards : []).findIndex((card) => Number(card.id) === Number(cardId));
        },
        isCompletedColumnExpanded(columnId) {
            return this.expandedCompletedColumnIds.includes(Number(columnId));
        },
        toggleCompletedColumn(columnId) {
            const normalizedColumnId = Number(columnId);
            if (this.isCompletedColumnExpanded(normalizedColumnId)) {
                this.expandedCompletedColumnIds = this.expandedCompletedColumnIds.filter((id) => Number(id) !== normalizedColumnId);
                return;
            }

            this.expandedCompletedColumnIds.push(normalizedColumnId);
        },
        getBoardCreator(board) {
            if (!board) {
                return null;
            }

            if (board.createdByUser) {
                return board.createdByUser;
            }

            return this.cardAssignees.find((item) => Number(item.id) === Number(board.createdById)) || null;
        },
        getBoardProject(board) {
            if (!board) {
                return null;
            }

            if (board.project) {
                return this.normalizeProject(board.project);
            }

            return this.boardProjects.find((project) => Number(project.id) === Number(board.projectId)) || null;
        },
        getBoardProjectImageUrl(board) {
            const project = this.getBoardProject(board);
            return project && project.imageUrl ? project.imageUrl : '/images/default/icon.png';
        },
        uniqueUsers(users) {
            const unique = [];
            const seen = new Set();

            (Array.isArray(users) ? users : []).forEach((user) => {
                const userId = Number(user && user.id);
                if (userId < 1 || seen.has(userId)) {
                    return;
                }

                seen.add(userId);
                unique.push({
                    ...user,
                    id: userId,
                });
            });

            return unique;
        },
        getAllKnownUsers() {
            const boardUsers = this.boards.flatMap((board) => [
                board.createdByUser,
                ...(Array.isArray(board.approvalUsers) ? board.approvalUsers : []),
                ...(Array.isArray(board.assignableUsers) ? board.assignableUsers : []),
                ...(Array.isArray(board.columns) ? board.columns.flatMap((column) => (
                    Array.isArray(column.cards) ? column.cards.flatMap((card) => (Array.isArray(card.assignees) ? card.assignees : [])) : []
                )) : []),
            ]);

            return this.uniqueUsers([
                ...this.cardAssignees,
                ...this.approvalInternalUsers,
                ...this.approvalCustomerUsers,
                ...this.approvalGuestUsers,
                ...boardUsers,
            ]);
        },
        getKnownUserById(userId) {
            const normalizedUserId = Number(userId);
            if (normalizedUserId < 1) {
                return null;
            }

            return this.getAllKnownUsers().find((user) => Number(user.id) === normalizedUserId) || null;
        },
        getBoardAssignableUsers(board) {
            if (!board) {
                return [];
            }

            if (Array.isArray(board.assignableUsers)) {
                return this.uniqueUsers(board.assignableUsers);
            }

            if (Array.isArray(this.cardAssignees) && this.cardAssignees.length) {
                return this.uniqueUsers(this.cardAssignees);
            }

            return this.uniqueUsers([
                this.getBoardCreator(board),
                ...this.getBoardApprovalUsers(board),
            ]);
        },
        getCardDialogAssignableUsers() {
            return this.getBoardAssignableUsers(this.activeBoard);
        },
        getBoardApprovalUsers(board) {
            if (!board) {
                return [];
            }

            if (Array.isArray(board.approvalUsers)) {
                return this.uniqueUsers(board.approvalUsers);
            }

            const selectedIds = [
                ...this.normalizeApprovalIds(board.approvalInternalIds),
                ...this.normalizeApprovalIds(board.approvalCustomerIds),
                ...this.normalizeApprovalIds(board.approvalGuestIds),
            ];
            const allUsers = [
                ...this.approvalInternalUsers,
                ...this.approvalCustomerUsers,
                ...this.approvalGuestUsers,
            ];
            const users = [];
            const seen = new Set();

            selectedIds.forEach((id) => {
                if (seen.has(id)) {
                    return;
                }

                const user = allUsers.find((item) => Number(item.id) === id);
                if (user) {
                    seen.add(id);
                    users.push(user);
                }
            });

            return users;
        },
        getVisibleBoardApprovalUsers(board) {
            return this.getBoardApprovalUsers(board).slice(0, 4);
        },
        getRemainingBoardApprovalUsersCount(board) {
            return Math.max(this.getBoardApprovalUsers(board).length - 4, 0);
        },
        getApprovalUserAvatar(user) {
            return this.getUserAvatar(user);
        },
        getUserAvatar(user) {
            if (user && user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }

            if (user && user.gravatar) {
                return user.gravatar;
            }

            return '/images/default/icon.png';
        },
        getUserInitials(user) {
            const name = user && user.name ? String(user.name).trim() : '';
            if (!name) {
                return '-';
            }

            return name
                .split(/\s+/)
                .slice(0, 2)
                .map((part) => part.charAt(0).toUpperCase())
                .join('');
        },
        getChecklistDoneCount(card) {
            return this.getChecklistItems(card).filter((item) => item.done).length;
        },
        getChecklistProgress(card) {
            const items = this.getChecklistItems(card);
            return this.getChecklistDoneCount(card) + '/' + items.length;
        },
        getVisibleChecklistItems(card) {
            const items = this.getChecklistItems(card);
            if (this.isChecklistExpanded(card.id)) {
                return items;
            }

            return items.slice(0, CHECKLIST_PREVIEW_LIMIT);
        },
        getHiddenChecklistCount(card) {
            const items = this.getChecklistItems(card);
            if (this.isChecklistExpanded(card.id)) {
                return 0;
            }

            return Math.max(0, items.length - CHECKLIST_PREVIEW_LIMIT);
        },
        hasChecklistToggle(card) {
            return this.getChecklistItems(card).length > CHECKLIST_PREVIEW_LIMIT;
        },
        isChecklistExpanded(cardId) {
            return this.expandedChecklistCardIds.includes(cardId);
        },
        toggleChecklistExpanded(cardId) {
            if (this.isChecklistExpanded(cardId)) {
                this.expandedChecklistCardIds = this.expandedChecklistCardIds.filter((id) => id !== cardId);
                return;
            }

            this.expandedChecklistCardIds.push(cardId);
        },
        toggleChecklistItem(columnId, cardId, itemId) {
            if (!this.activeBoardCanManage) {
                return;
            }

            const card = this.findCard(columnId, cardId);
            if (!card || !Array.isArray(card.checklist)) {
                return;
            }

            const item = card.checklist.find((entry) => entry.id === itemId);
            if (!item) {
                return;
            }

            item.done = !item.done;
            axios.put('api/workspace/boards/checklist-items/' + item.id + '/toggle')
                .then((response) => {
                    this.syncBoardFromResponse(response);
                })
                .catch(() => {
                    item.done = !item.done;
                    this.notifyError(this.$t('An error occurred while saving data').toString());
                });
        },
        getCardAssignees(card) {
            const assigneeIds = this.normalizeApprovalIds(Array.isArray(card && card.assigneeIds) ? card.assigneeIds : (card && card.assigneeId ? [card.assigneeId] : []));
            const cardAssignees = this.uniqueUsers(Array.isArray(card && card.assignees) ? card.assignees : (card && card.assignee ? [card.assignee] : []));

            return assigneeIds
                .map((assigneeId) => this.getKnownUserById(assigneeId) || cardAssignees.find((assignee) => Number(assignee.id) === assigneeId))
                .filter((assignee) => !!assignee)
                .map((assignee) => ({
                    ...assignee,
                    initials: this.getUserInitials(assignee),
                    avatarUrl: this.getUserAvatar(assignee),
                }));
        },
        getVisibleCardAssignees(card) {
            return this.getCardAssignees(card).slice(0, 4);
        },
        getRemainingCardAssigneeCount(card) {
            return Math.max(this.getCardAssignees(card).length - 4, 0);
        },
        hasCardFooter(card) {
            return this.getCardAssignees(card).length > 0
                || !!(card && (card.dueDate || card.completedAt || Number(card.attachmentCount) > 0));
        },
        getCardItemClass(card) {
            const verticalPadding = card && card.description ? 'pt-2.5' : 'pt-2';
            const bottomPadding = this.hasCardFooter(card) ? 'pb-0' : (card && card.description ? 'pb-2.5' : 'pb-2');

            return [
                verticalPadding,
                bottomPadding,
                this.activeBoardCanManage ? 'board-drag-surface' : 'cursor-default',
            ];
        },
        formatCardDate(value) {
            if (!value) {
                return '';
            }

            const normalizedValue = String(value).slice(0, 10);
            const parts = normalizedValue.split('-');
            if (parts.length !== 3) {
                return value;
            }

            return parts[2] + '.' + parts[1] + '.' + parts[0];
        },
        isCardOverdue(card) {
            if (!card || card.completed || !card.dueDate) {
                return false;
            }

            return card.dueDate < this.todayDateString();
        },
        todayDateString() {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return year + '-' + month + '-' + day;
        },
        getColumnColor(column) {
            return this.normalizePresetColor(column && column.color);
        },
        activateBoard(boardId) {
            boardId = Number(boardId);
            this.closeBoardActionsMenu();
            this.closeBoardFilterMenu();
            this.closeBoardSelectMenu();
            this.closeColumnActionsMenu();
            this.closeCardActionsMenu();
            this.closeApprovalDialog();

            const board = this.boards.find((item) => item.id === boardId);
            if (!board) {
                return;
            }

            if (!board.detailsLoaded) {
                this.loadBoards({activeBoardId: boardId});
                return;
            }

            this.activeBoardId = boardId;
        },
        selectBoardFromMenu(boardId) {
            boardId = Number(boardId);
            if (boardId < 1) {
                return;
            }

            this.activateBoard(boardId);
        },
        toggleBoardSelectMenu() {
            this.boardSelectMenuOpen = !this.boardSelectMenuOpen;
            if (this.boardSelectMenuOpen) {
                this.closeBoardFilterMenu();
            }
        },
        closeBoardSelectMenu() {
            this.boardSelectMenuOpen = false;
        },
        toggleBoardFilterMenu() {
            this.boardFilterMenuOpen = !this.boardFilterMenuOpen;
            if (this.boardFilterMenuOpen) {
                this.closeBoardSelectMenu();
            }
        },
        closeBoardFilterMenu() {
            this.boardFilterMenuOpen = false;
        },
        setBoardFilter(filter) {
            this.boardFilter = ['all', 'mine', 'shared', 'archived'].includes(filter) ? filter : 'all';
            this.closeBoardFilterMenu();
            this.closeBoardActionsMenu();
            this.loadBoards();
        },
        toggleBoardActionsMenu() {
            if (!this.activeBoardCanManage) {
                return;
            }
            this.boardActionsOpen = !this.boardActionsOpen;
        },
        closeBoardActionsMenu() {
            this.boardActionsOpen = false;
        },
        toggleColumnActionsMenu(columnId) {
            if (!this.activeBoardCanManage) {
                return;
            }
            this.columnActionsOpenId = this.columnActionsOpenId === columnId ? null : columnId;
        },
        closeColumnActionsMenu() {
            this.columnActionsOpenId = null;
        },
        toggleCardActionsMenu(cardId, event = null, useFixedPosition = false) {
            if (!this.activeBoardCanManage) {
                return;
            }

            if (this.cardActionsOpenId === cardId) {
                this.closeCardActionsMenu();
                return;
            }

            this.cardActionsOpenId = cardId;
            this.cardActionsMenuFixed = !!useFixedPosition;
            this.cardActionsMenuStyle = useFixedPosition
                ? this.getFixedCardActionsMenuStyle(event)
                : null;
        },
        closeCardActionsMenu() {
            this.cardActionsOpenId = null;
            this.cardActionsMenuFixed = false;
            this.cardActionsMenuStyle = null;
        },
        getFixedCardActionsMenuStyle(event) {
            const trigger = event && event.currentTarget;
            if (!trigger || typeof trigger.getBoundingClientRect !== 'function' || typeof window === 'undefined') {
                return null;
            }

            const rect = trigger.getBoundingClientRect();
            const viewportPadding = 8;
            const menuGap = 4;
            const estimatedMenuHeight = 128;
            const openAbove = window.innerHeight - rect.bottom < estimatedMenuHeight + viewportPadding;

            return {
                right: Math.max(viewportPadding, window.innerWidth - rect.right) + 'px',
                top: openAbove ? 'auto' : (rect.bottom + menuGap) + 'px',
                bottom: openAbove ? (window.innerHeight - rect.top + menuGap) + 'px' : 'auto',
                transformOrigin: openAbove ? 'bottom right' : 'top right',
            };
        },
        toggleBoardProjectDropdown() {
            this.boardProjectDropdownOpen = !this.boardProjectDropdownOpen;
        },
        closeBoardProjectDropdown() {
            this.boardProjectDropdownOpen = false;
        },
        selectBoardProject(project) {
            this.boardDialogProjectId = project && project.id ? String(project.id) : '';
            this.closeBoardProjectDropdown();
        },
        openBoardDialog(mode, board = null) {
            if (mode === 'create' && !this.canCreateBoards) {
                return;
            }

            if (mode === 'rename' && (!board || !board.canManage)) {
                return;
            }

            this.boardDialogMode = mode;
            this.boardDialogBoardId = board ? board.id : null;
            this.boardDialogTitle = board ? board.title : '';
            this.boardDialogProjectId = board && board.projectId ? String(board.projectId) : '';
            this.boardProjectDropdownOpen = false;
            this.boardDialogOpen = true;
            this.$nextTick(() => {
                if (this.$refs.boardDialogInput) {
                    this.$refs.boardDialogInput.focus();
                }
            });
        },
        closeBoardDialog() {
            this.boardDialogOpen = false;
            this.boardDialogMode = 'create';
            this.boardDialogBoardId = null;
            this.boardDialogTitle = '';
            this.boardDialogProjectId = '';
            this.boardProjectDropdownOpen = false;
        },
        saveBoardDialog() {
            if (this.boardDialogMode === 'create' && !this.canCreateBoards) {
                return;
            }

            if (this.boardDialogMode === 'rename') {
                const board = this.boards.find((item) => item.id === this.boardDialogBoardId);
                if (!board || !board.canManage) {
                    return;
                }
            }

            if (!this.boardDialogTitle) {
                this.notifyValidationError(this.$t('Please fill in all required fields correctly.').toString(), 'boardDialogInput');
                return;
            }

            const payload = {
                title: this.boardDialogTitle,
                project_id: this.boardDialogProjectId ? Number(this.boardDialogProjectId) : null,
            };

            if (this.boardDialogMode === 'rename') {
                axios.put('api/workspace/boards/' + this.boardDialogBoardId, payload)
                    .then((response) => {
                        this.syncBoardFromResponse(response);
                        this.closeBoardDialog();
                    })
                    .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()));
            } else {
                axios.post('api/workspace/boards', payload)
                    .then((response) => {
                        const boardId = Number(response.data && response.data.board ? response.data.board.id : 0) || null;
                        this.closeBoardDialog();
                        this.loadBoards({activeBoardId: boardId, filter: 'mine'});
                    })
                    .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()));
            }
        },
        requestRemoveActiveBoard() {
            if (!this.activeBoardCanManage || !this.activeBoard.canDelete) {
                return;
            }

            this.openDeleteDialog(
                'board',
                this.$t('Delete board').toString(),
                this.$t('Do you really want to delete this board? This action cannot be undone.').toString(),
                {boardId: this.activeBoard.id},
            );
        },
        removeActiveBoard(boardId = null) {
            const targetBoardId = boardId || (this.activeBoard ? this.activeBoard.id : null);
            const targetBoard = this.boards.find((board) => board.id === targetBoardId);

            if (!targetBoard || !targetBoard.canManage || !targetBoard.canDelete) {
                return;
            }

            const targetIndex = this.boards.findIndex((board) => board.id === targetBoardId);
            if (targetIndex < 0) {
                return;
            }

            axios.delete('api/workspace/boards/' + targetBoardId)
                .then(() => {
                    const remainingBoards = this.boards.filter((board) => board.id !== targetBoardId);
                    const nextBoard = remainingBoards[Math.max(0, targetIndex - 1)] || remainingBoards[0] || null;
                    this.closeBoardDialog();
                    this.closeColumnDialog();
                    this.closeCardDialog();
                    this.closeCardActionsMenu();
                    this.loadBoards({
                        activeBoardId: nextBoard ? nextBoard.id : null,
                        filter: this.boardFilter,
                    });
                })
                .catch(() => this.notifyError(this.$t('An error occurred while deleting data').toString()));
        },
        toggleArchiveActiveBoard() {
            if (!this.activeBoard || !this.activeBoard.canArchive) {
                return;
            }

            axios.put('api/workspace/boards/' + this.activeBoard.id + '/archive')
                .then((response) => {
                    const board = response && response.data ? response.data.board : null;
                    const boardId = board && board.id ? Number(board.id) : this.activeBoard.id;
                    const archived = !!(board && board.archived);
                    const nextFilter = archived ? 'archived' : 'mine';

                    this.loadBoards({
                        activeBoardId: boardId,
                        filter: nextFilter,
                    });
                })
                .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()));
        },
        openApprovalDialog() {
            if (!this.activeBoard || !this.activeBoard.canShare) {
                return;
            }

            this.closeBoardActionsMenu();
            const boardId = Number(this.activeBoard.id);
            this.approvalInternalUsers = [];
            this.approvalCustomerUsers = [];
            this.approvalGuestUsers = [];
            this.approvalEmptyCustomerText = this.$t('No customer users available.').toString();

            axios.get('api/workspace/boards/' + boardId + '/approval-options').then((response) => {
                if (!this.activeBoard || Number(this.activeBoard.id) !== boardId) {
                    return;
                }

                const options = response && response.data ? response.data : {};
                this.approvalDialogScope = options.scope === 'customer' ? 'customer' : 'full';
                this.approvalDialogShowTabs = options.show_tabs !== false;
                this.approvalDialogInitialTab = options.initial_tab === 'customer' ? 'customer' : 'internal';
                this.approvalEmptyCustomerText = options.empty_customer_text
                    ? String(options.empty_customer_text)
                    : this.$t('No customer users available.').toString();
                this.approvalInternalUsers = Array.isArray(options.internal) ? options.internal : [];
                this.approvalCustomerUsers = Array.isArray(options.customers) ? options.customers : [];
                this.approvalGuestUsers = [];
                this.approvalDialogOpen = true;
            }).catch((error) => this.notifyRequestError(
                error,
                this.$t('An error occurred while loading data').toString()
            ));
        },
        closeApprovalDialog() {
            this.approvalDialogOpen = false;
        },
        saveApprovalDialog(payload = null) {
            if (!this.activeBoard || !this.activeBoard.canShare) {
                return;
            }

            const internalPermissions = Array.isArray(payload && payload.internal_permissions)
                ? payload.internal_permissions
                : [];
            const customerPermissions = Array.isArray(payload && payload.customer_permissions)
                ? payload.customer_permissions
                : [];

            const requestPayload = this.approvalDialogScope === 'customer'
                ? {customer_permissions: customerPermissions}
                : {
                    internal_permissions: internalPermissions,
                    customer_permissions: customerPermissions,
                };

            axios.put('api/workspace/boards/' + this.activeBoard.id + '/approvals', requestPayload).then((response) => {
                const savedBoard = response && response.data ? response.data.board : null;
                const savedCustomerPermissions = this.approvalDialogScope === 'customer'
                    ? this.filterApprovalPermissionsForUsers(
                        savedBoard && savedBoard.approvalCustomerPermissions,
                        this.approvalCustomerUsers
                    )
                    : (savedBoard && savedBoard.approvalCustomerPermissions);
                const permissionsMatch = this.approvalDialogScope === 'customer'
                    ? this.approvalPermissionsMatch(customerPermissions, savedCustomerPermissions)
                    : this.approvalPermissionsMatch(internalPermissions, savedBoard && savedBoard.approvalInternalPermissions)
                        && this.approvalPermissionsMatch(customerPermissions, savedCustomerPermissions);

                if (!savedBoard || !permissionsMatch) {
                    this.syncBoardFromResponse(response);
                    this.notifyError(this.$t('An error occurred while saving data').toString());
                    return;
                }

                this.syncBoardFromResponse(response);
                this.approvalDialogOpen = false;
                this.notifySuccess(this.$t('Data updated correctly').toString());
            }).catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()));
        },
        approvalPermissionsMatch(requestedPermissions, savedPermissions) {
            const normalize = (entries) => {
                const permissions = new Map();

                if (Array.isArray(entries)) {
                    entries.forEach((entry) => {
                        const userId = Number(entry && entry.user_id);
                        const permission = entry && entry.permission;
                        if (userId > 0 && ['read', 'edit'].includes(permission)) {
                            permissions.set(userId, permission);
                        }
                    });
                } else {
                    Object.entries(entries && typeof entries === 'object' ? entries : {}).forEach(([userId, permission]) => {
                        const normalizedUserId = Number(userId);
                        if (normalizedUserId > 0 && ['read', 'edit'].includes(permission)) {
                            permissions.set(normalizedUserId, permission);
                        }
                    });
                }

                return Array.from(permissions.entries())
                    .sort(([leftUserId], [rightUserId]) => leftUserId - rightUserId);
            };

            return JSON.stringify(normalize(requestedPermissions)) === JSON.stringify(normalize(savedPermissions));
        },
        filterApprovalPermissionsForUsers(permissions, users) {
            const allowedIds = new Set((Array.isArray(users) ? users : [])
                .map((user) => Number(user && user.id))
                .filter((userId) => userId > 0));

            return Object.entries(permissions && typeof permissions === 'object' ? permissions : {})
                .reduce((filtered, [userId, permission]) => {
                    const normalizedUserId = Number(userId);
                    if (allowedIds.has(normalizedUserId) && ['read', 'edit'].includes(permission)) {
                        filtered[normalizedUserId] = permission;
                    }
                    return filtered;
                }, {});
        },
        openColumnDialog(mode, column = null) {
            if (!this.activeBoardCanManage) {
                return;
            }

            this.columnDialogMode = mode;
            this.columnDialogColumnId = column ? column.id : null;
            this.columnDialogTitle = column ? column.title : '';
            this.columnDialogColor = column ? this.getColumnColor(column) : DEFAULT_COLUMN_COLOR;
            this.columnDialogIsProtected = !!(column && column.isProtected);
            this.columnDialogOpen = true;
            this.$nextTick(() => {
                if (this.$refs.columnDialogInput) {
                    this.$refs.columnDialogInput.focus();
                }
            });
        },
        closeColumnDialog() {
            this.columnDialogOpen = false;
            this.columnDialogMode = 'create';
            this.columnDialogColumnId = null;
            this.columnDialogTitle = '';
            this.columnDialogColor = DEFAULT_COLUMN_COLOR;
            this.columnDialogIsProtected = false;
        },
        saveColumnDialog() {
            if (!this.activeBoardCanManage || !this.columnDialogTitle) {
                this.notifyValidationError(this.$t('Please fill in all required fields correctly.').toString(), 'columnDialogInput');
                return;
            }

            const payload = {
                title: this.columnDialogTitle,
                color: this.normalizePresetColor(this.columnDialogColor) || null,
            };

            if (this.columnDialogMode === 'rename') {
                axios.put('api/workspace/boards/columns/' + this.columnDialogColumnId, payload)
                    .then((response) => {
                        this.syncBoardFromResponse(response);
                        this.closeColumnDialog();
                    })
                    .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()));
            } else {
                axios.post('api/workspace/boards/' + this.activeBoard.id + '/columns', payload)
                    .then((response) => {
                        this.syncBoardFromResponse(response);
                        this.closeColumnDialog();
                    })
                    .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()));
            }
        },
        requestRemoveColumn(columnId) {
            if (!this.activeBoardCanManage) {
                return;
            }

            const column = this.findColumn(columnId);
            if (!column || !column.canDelete) {
                return;
            }

            this.openDeleteDialog(
                'column',
                this.$t('Delete column').toString(),
                this.$t('Do you really want to delete this column? All cards in this column will be deleted too.').toString(),
                {columnId},
            );
        },
        removeColumn(columnId) {
            if (!this.activeBoardCanManage) {
                return;
            }

            const column = this.findColumn(columnId);
            if (!column || !column.canDelete) {
                return;
            }

            axios.delete('api/workspace/boards/columns/' + columnId)
                .then((response) => this.syncBoardFromResponse(response))
                .catch(() => this.notifyError(this.$t('An error occurred while deleting data').toString()));
        },
        openColumnDialogFromDoubleClick(column, event) {
            if (!this.activeBoardCanManage || this.shouldIgnoreEditDoubleClick(event)) {
                return;
            }

            this.openColumnDialog('rename', column);
        },
        openCardDialogFromDoubleClick(columnId, card, event) {
            if (!this.activeBoardCanManage || this.shouldIgnoreEditDoubleClick(event)) {
                return;
            }

            this.openCardDialog(columnId, card);
        },
        shouldIgnoreEditDoubleClick(event) {
            const target = event && event.target;
            return !!(target && typeof target.closest === 'function' && target.closest('button, a, input, textarea, select, label, [role="button"]'));
        },
        openCardDialog(columnId, card = null) {
            if (!this.activeBoardCanManage) {
                return;
            }

            const assignableUsers = this.getCardDialogAssignableUsers();
            const assignableUserIds = assignableUsers.map((assignee) => Number(assignee.id));
            const assigneeIds = this.normalizeApprovalIds(Array.isArray(card && card.assigneeIds) ? card.assigneeIds : (card && card.assigneeId ? [card.assigneeId] : []));

            this.cardDialogMode = card ? 'edit' : 'create';
            this.cardDialogColumnId = columnId;
            this.cardDialogCardId = card ? card.id : null;
            this.cardDialogTitle = card ? card.title : '';
            this.cardDialogDescription = card ? card.description : '';
            this.cardDialogColor = card ? this.normalizeCardColor(card.color) : '';
            this.cardDialogAssigneeIds = assigneeIds.filter((assigneeId) => assignableUserIds.includes(assigneeId));
            this.cardDialogDueDate = card && card.dueDate ? card.dueDate : '';
            this.cardDialogCompleted = !!(card && card.completed);
            this.cardDialogChecklist = card ? this.cloneChecklist(card.checklist) : [];
            this.cardDialogChecklistTitle = '';
            this.cardDialogAttachments = [];
            this.cardDialogDraftAttachmentIds = [];
            this.cardDialogAttachmentsLoading = !!card;
            this.cardDialogAttachmentsLoaded = !card;
            this.cardDialogUploadBusy = false;
            this.cardDialogAttachmentMutationBusy = false;
            this.cardDialogSaving = false;
            this.cardDialogUploadProgress = 0;
            this.cardDialogOpen = true;
            if (card) {
                this.loadCardDialogAttachments(card.id);
            }
            this.$nextTick(() => {
                if (this.$refs.cardDialogInput) {
                    this.$refs.cardDialogInput.focus();
                }
            });
        },
        closeCardDialog() {
            if (this.cardDialogSaving || this.cardDialogUploadBusy || this.cardDialogAttachmentMutationBusy) {
                this.notifyError(this.$t('Please wait until all files have been uploaded.').toString());
                return;
            }

            this.cleanupCardDialogDraftAttachments();
            this.cardDialogOpen = false;
            this.cardDialogMode = 'create';
            this.cardDialogColumnId = null;
            this.cardDialogCardId = null;
            this.cardDialogTitle = '';
            this.cardDialogDescription = '';
            this.cardDialogColor = '';
            this.cardDialogAssigneeIds = [];
            this.cardDialogDueDate = '';
            this.cardDialogCompleted = false;
            this.cardDialogChecklist = [];
            this.cardDialogChecklistTitle = '';
            this.cardDialogAttachments = [];
            this.cardDialogAttachmentsLoading = false;
            this.cardDialogAttachmentsLoaded = true;
            this.cardDialogAttachmentMutationBusy = false;
            this.cardDialogUploadProgress = 0;
        },
        loadCardDialogAttachments(cardId) {
            axios.get('api/workspace/boards/cards/' + cardId + '/attachments')
                .then((response) => {
                    if (!this.cardDialogOpen || Number(this.cardDialogCardId) !== Number(cardId)) {
                        return;
                    }

                    this.cardDialogAttachments = Array.isArray(response.data && response.data.attachments)
                        ? response.data.attachments
                        : [];
                    this.cardDialogAttachmentsLoaded = true;
                })
                .catch((error) => {
                    if (!this.cardDialogOpen || Number(this.cardDialogCardId) !== Number(cardId)) {
                        return;
                    }

                    this.cardDialogAttachmentsLoaded = false;
                    this.notifyRequestError(error, this.$t('An error occurred while loading data').toString());
                })
                .finally(() => {
                    if (this.cardDialogOpen && Number(this.cardDialogCardId) === Number(cardId)) {
                        this.cardDialogAttachmentsLoading = false;
                    }
                });
        },
        triggerCardDialogFileInput() {
            if (this.cardDialogSaving || this.cardDialogUploadBusy || this.cardDialogAttachmentMutationBusy || this.cardDialogAttachmentsLoading || !this.$refs.cardDialogFileInput) {
                return;
            }

            this.$refs.cardDialogFileInput.click();
        },
        async uploadCardDialogFiles(event) {
            const files = Array.from(event && event.target && event.target.files ? event.target.files : []);
            if (event && event.target) {
                event.target.value = null;
            }
            if (!files.length || !this.cardDialogColumnId || this.cardDialogSaving || this.cardDialogUploadBusy) {
                return;
            }

            const availableSlots = Math.max(0, MAX_CARD_ATTACHMENTS - this.cardDialogAttachments.length);
            if (availableSlots === 0 || files.length > availableSlots) {
                this.notifyError(this.$t('A maximum of 20 file attachments is allowed.').toString());
                return;
            }

            this.cardDialogUploadBusy = true;
            this.cardDialogUploadProgress = 0;

            for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
                const formData = new FormData();
                formData.append('file', files[fileIndex]);

                try {
                    const response = await axios.post(
                        'api/workspace/boards/columns/' + this.cardDialogColumnId + '/attachments',
                        formData,
                        {
                            onUploadProgress: (progressEvent) => {
                                const fileProgress = progressEvent.total
                                    ? progressEvent.loaded / progressEvent.total
                                    : 0;
                                this.cardDialogUploadProgress = Math.round(((fileIndex + fileProgress) / files.length) * 100);
                            },
                        }
                    );

                    if (!this.cardDialogOpen) {
                        await axios.delete('api/workspace/boards/attachments/' + response.data.uuid);
                        continue;
                    }

                    this.cardDialogAttachments.push(response.data);
                    this.cardDialogDraftAttachmentIds.push(Number(response.data.id));
                } catch (error) {
                    this.notifyRequestError(error, this.$t('An error occurred while saving data').toString());
                }
            }

            this.cardDialogUploadProgress = 100;
            this.cardDialogUploadBusy = false;
            this.$nextTick(() => {
                this.cardDialogUploadProgress = 0;
            });
        },
        removeCardDialogAttachment(attachmentIndex) {
            if (this.cardDialogSaving) {
                return;
            }

            const attachment = this.cardDialogAttachments[attachmentIndex];
            if (!attachment) {
                return;
            }

            const attachmentId = Number(attachment.id);
            if (!this.cardDialogDraftAttachmentIds.includes(attachmentId)) {
                this.cardDialogAttachments.splice(attachmentIndex, 1);
                return;
            }

            this.cardDialogAttachmentMutationBusy = true;
            axios.delete('api/workspace/boards/attachments/' + attachment.uuid)
                .then(() => {
                    this.cardDialogAttachments = this.cardDialogAttachments.filter((item) => Number(item.id) !== attachmentId);
                    this.cardDialogDraftAttachmentIds = this.cardDialogDraftAttachmentIds.filter((id) => Number(id) !== attachmentId);
                })
                .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while deleting data').toString()))
                .finally(() => {
                    this.cardDialogAttachmentMutationBusy = false;
                });
        },
        cleanupCardDialogDraftAttachments() {
            const draftAttachments = this.cardDialogAttachments.filter((attachment) => (
                this.cardDialogDraftAttachmentIds.includes(Number(attachment.id)) && attachment.uuid
            ));

            draftAttachments.forEach((attachment) => {
                axios.delete('api/workspace/boards/attachments/' + attachment.uuid).catch(() => {});
            });
            this.cardDialogDraftAttachmentIds = [];
        },
        getDialogChecklistProgress() {
            const items = this.normalizeChecklist(this.cardDialogChecklist);
            const done = items.filter((item) => item.done).length;
            return done + '/' + items.length;
        },
        addChecklistDialogItem() {
            const input = this.$refs.cardDialogChecklistInput;
            const title = String(input && typeof input.value === 'string'
                ? input.value
                : this.cardDialogChecklistTitle || '').trim();

            if (!title) {
                this.focusChecklistDialogInput();
                return;
            }

            this.cardDialogChecklist = [...this.cardDialogChecklist, {
                id: this.createDialogChecklistId(),
                title,
                done: false,
            }];
            this.cardDialogChecklistTitle = '';
            this.focusChecklistDialogInput(true);
        },
        removeChecklistDialogItem(itemId) {
            this.cardDialogChecklist = this.cardDialogChecklist.filter((item) => item.id !== itemId);
        },
        focusChecklistInputOrAdd(itemIndex) {
            if (itemIndex >= this.cardDialogChecklist.length - 1) {
                this.focusChecklistDialogInput();
            }
        },
        focusChecklistDialogInput(clearValue = false) {
            this.$nextTick(() => {
                if (this.$refs.cardDialogChecklistInput) {
                    if (clearValue) {
                        this.$refs.cardDialogChecklistInput.value = '';
                    }
                    this.$refs.cardDialogChecklistInput.focus();
                }
            });
        },
        createDialogChecklistId() {
            const minimumId = this.cardDialogChecklist.reduce((minimum, item) => {
                const itemId = Number(item && item.id);
                return Number.isFinite(itemId) ? Math.min(minimum, itemId) : minimum;
            }, 0);

            return minimumId - 1;
        },
        saveCardDialog() {
            if (!this.activeBoardCanManage) {
                return;
            }

            if (this.cardDialogSaving || this.cardDialogUploadBusy || this.cardDialogAttachmentMutationBusy || this.cardDialogAttachmentsLoading || !this.cardDialogAttachmentsLoaded) {
                this.notifyError(this.$t('Please wait until all files have been uploaded.').toString());
                return;
            }

            if (!this.cardDialogTitle) {
                this.notifyValidationError(this.$t('Please fill in all required fields correctly.').toString(), 'cardDialogInput');
                return;
            }

            if (!this.isCardDialogDueDateValid()) {
                this.notifyValidationError(this.$t('Please enter a valid due date.').toString(), 'cardDialogDueDateInput');
                return;
            }

            const payload = {
                title: this.cardDialogTitle,
                description: this.cardDialogDescription,
                color: this.normalizeCardColor(this.cardDialogColor) || null,
                assignee_ids: this.normalizeApprovalIds(this.cardDialogAssigneeIds),
                due_date: this.cardDialogDueDate || null,
                completed: !!this.cardDialogCompleted,
                checklist: this.cloneChecklist(this.cardDialogChecklist),
                attachments: this.cardDialogAttachments.map((attachment) => ({id: Number(attachment.id)})),
            };
            this.cardDialogSaving = true;
            if (this.cardDialogMode === 'edit') {
                axios.put('api/workspace/boards/cards/' + this.cardDialogCardId, payload)
                    .then((response) => {
                        this.cardDialogSaving = false;
                        this.cardDialogDraftAttachmentIds = [];
                        this.syncBoardFromResponse(response);
                        this.closeCardDialog();
                    })
                    .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()))
                    .finally(() => {
                        this.cardDialogSaving = false;
                    });
            } else {
                axios.post('api/workspace/boards/columns/' + this.cardDialogColumnId + '/cards', payload)
                    .then((response) => {
                        this.cardDialogSaving = false;
                        this.cardDialogDraftAttachmentIds = [];
                        this.syncBoardFromResponse(response);
                        this.closeCardDialog();
                    })
                    .catch((error) => this.notifyRequestError(error, this.$t('An error occurred while saving data').toString()))
                    .finally(() => {
                        this.cardDialogSaving = false;
                    });
            }
        },
        requestRemoveCard(columnId, cardId) {
            if (!this.activeBoardCanManage) {
                return;
            }

            this.openDeleteDialog(
                'card',
                this.$t('Delete card').toString(),
                this.$t('Do you really want to delete this card? This action cannot be undone.').toString(),
                {columnId, cardId},
            );
        },
        removeCard(columnId, cardId) {
            if (!this.activeBoardCanManage) {
                return;
            }

            const column = this.findColumn(columnId);
            if (!column) {
                return;
            }

            axios.delete('api/workspace/boards/cards/' + cardId)
                .then((response) => this.syncBoardFromResponse(response))
                .catch(() => this.notifyError(this.$t('An error occurred while deleting data').toString()));
        },
        toggleCardCompletion(columnId, cardId) {
            if (!this.activeBoardCanManage) {
                return;
            }

            const card = this.findCard(columnId, cardId);
            if (!card) {
                return;
            }

            const previousCompleted = !!card.completed;
            const previousCompletedAt = card.completedAt || '';
            card.completed = !previousCompleted;
            card.completedAt = card.completed ? this.todayDateString() : '';

            axios.put('api/workspace/boards/cards/' + cardId + '/completion')
                .then((response) => this.syncBoardFromResponse(response))
                .catch(() => {
                    card.completed = previousCompleted;
                    card.completedAt = previousCompletedAt;
                    this.notifyError(this.$t('An error occurred while saving data').toString());
                });
        },
        openDeleteDialog(type, title, text, payload) {
            this.deleteDialog = {
                open: true,
                type,
                title,
                text,
                payload,
            };
        },
        closeDeleteDialog() {
            this.deleteDialog = {
                open: false,
                type: null,
                title: '',
                text: '',
                payload: null,
            };
        },
        syncBoardFromResponse(response) {
            const board = response && response.data ? response.data.board : null;
            if (!board) {
                return;
            }

            this.replaceBoard(this.normalizeBoards([board])[0]);
        },
        replaceBoard(board) {
            if (!board) {
                return;
            }

            const index = this.boards.findIndex((item) => item.id === board.id);
            if (index >= 0) {
                this.boards.splice(index, 1, board);
            } else {
                this.boards.push(board);
            }
            this.applyBoardDeleteAvailability();
        },
        persistBoardLayout() {
            if (!this.activeBoardCanManage) {
                return;
            }

            axios.put('api/workspace/boards/' + this.activeBoard.id + '/layout', {
                column_ids: this.activeBoard.columns.map((column) => column.id),
                columns: this.activeBoard.columns.map((column) => ({
                    id: column.id,
                    card_ids: column.cards.map((card) => card.id),
                })),
            }).then((response) => {
                this.syncBoardFromResponse(response);
            }).catch(() => {
                this.notifyError(this.$t('An error occurred while saving data').toString());
                this.loadBoards();
            });
        },
        notifyError(text) {
            if (this.$notify) {
                this.$notify({
                    title: this.$t('Error').toString(),
                    text,
                    type: 'error',
                });
            }
        },
        notifySuccess(text) {
            if (this.$notify) {
                this.$notify({
                    title: this.$t('Success').toString(),
                    text,
                    type: 'success',
                });
            }
        },
        notifyValidationError(text, refName = null) {
            this.notifyError(text);
            if (!refName || !this.$refs[refName]) {
                return;
            }

            this.$nextTick(() => {
                if (this.$refs[refName] && typeof this.$refs[refName].focus === 'function') {
                    this.$refs[refName].focus();
                }
            });
        },
        notifyRequestError(error, fallbackText) {
            const message = error
                && error.response
                && error.response.data
                && error.response.data.message
                ? String(error.response.data.message)
                : fallbackText;

            this.notifyError(message);
        },
        isCardDialogDueDateValid() {
            const input = this.$refs.cardDialogDueDateInput;
            if (input && typeof input.checkValidity === 'function' && !input.checkValidity()) {
                return false;
            }

            if (!this.cardDialogDueDate) {
                return true;
            }

            return /^\d{4}-\d{2}-\d{2}$/.test(String(this.cardDialogDueDate));
        },
        confirmDelete() {
            const {type, payload} = this.deleteDialog;

            if (type === 'board' && payload) {
                this.removeActiveBoard(payload.boardId);
            }

            if (type === 'column' && payload) {
                this.removeColumn(payload.columnId);
            }

            if (type === 'card' && payload) {
                this.removeCard(payload.columnId, payload.cardId);
            }

            this.closeDeleteDialog();
        },
        findColumn(columnId) {
            if (!this.activeBoard) {
                return null;
            }

            return this.activeBoard.columns.find((column) => column.id === columnId) || null;
        },
        findCard(columnId, cardId) {
            const column = this.findColumn(columnId);
            if (!column || !Array.isArray(column.cards)) {
                return null;
            }

            return column.cards.find((card) => card.id === cardId) || null;
        },
        startColumnDrag(columnId, event) {
            if (!this.activeBoardCanManage) {
                return;
            }

            this.draggingColumnId = columnId;
            this.draggingColumnOverId = null;
            this.draggingColumnMoved = false;
            this.draggingCard = null;
            this.closeColumnActionsMenu();
            this.closeCardActionsMenu();
            this.setBoardDraggingCursor();

            if (event && event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', columnId);

                const columnElement = event.currentTarget && typeof event.currentTarget.closest === 'function'
                    ? event.currentTarget.closest('[data-board-column-id]')
                    : null;
                if (columnElement && typeof event.dataTransfer.setDragImage === 'function') {
                    const rect = columnElement.getBoundingClientRect();
                    const offsetX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
                    const offsetY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
                    event.dataTransfer.setDragImage(columnElement, offsetX, offsetY);
                }
            }
        },
        endColumnDrag() {
            this.finishColumnDrag();
        },
        finishColumnDrag() {
            if (!this.draggingColumnId) {
                return;
            }

            const shouldPersist = this.draggingColumnMoved;
            this.draggingColumnId = null;
            this.draggingColumnOverId = null;
            this.draggingColumnMoved = false;
            this.clearBoardDraggingCursor();

            if (shouldPersist) {
                this.persistBoardLayout();
            }
        },
        handleColumnBodyDragOver(columnId, event) {
            if (this.draggingColumnId) {
                this.moveColumnOver(columnId, event);
                return;
            }

            this.markColumnDragTarget(columnId);
        },
        markColumnDragTarget(columnId) {
            if (!this.activeBoardCanManage || this.draggingColumnId || !this.draggingCard) {
                this.draggingColumnOverId = null;
                return;
            }

            this.draggingColumnOverId = columnId;
        },
        moveColumnOver(targetColumnId, event) {
            if (!this.activeBoardCanManage || !this.draggingColumnId) {
                return;
            }

            const columns = this.activeBoard.columns;
            const sourceIndex = columns.findIndex((column) => column.id === this.draggingColumnId);
            const targetIndex = columns.findIndex((column) => column.id === targetColumnId);

            if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
                return;
            }

            this.moveColumnToIndex(targetIndex);
        },
        moveColumnToPointer(event) {
            if (!this.activeBoardCanManage || !this.draggingColumnId || !event || !event.currentTarget) {
                return;
            }

            const track = typeof event.currentTarget.closest === 'function'
                ? (event.currentTarget.closest('[data-board-columns-track]') || event.currentTarget)
                : event.currentTarget;
            const columnElements = Array.from(track.querySelectorAll('[data-board-column-id]'))
                .filter((element) => Number(element.getAttribute('data-board-column-id')) !== Number(this.draggingColumnId));

            if (!columnElements.length) {
                this.moveColumnToIndex(0);
                return;
            }

            const nextIndex = columnElements.findIndex((element) => {
                const rect = element.getBoundingClientRect();
                return event.clientX < rect.left + (rect.width / 2);
            });

            this.moveColumnToIndex(nextIndex < 0 ? columnElements.length : nextIndex);
        },
        moveColumnToEnd() {
            if (!this.activeBoardCanManage || !this.draggingColumnId || !this.activeBoard) {
                return;
            }

            this.moveColumnToIndex(Math.max(0, this.activeBoard.columns.length - 1));
        },
        moveColumnToIndex(nextIndex) {
            if (!this.activeBoardCanManage || !this.draggingColumnId || !this.activeBoard) {
                return;
            }

            const columns = this.activeBoard.columns;
            const sourceIndex = columns.findIndex((column) => column.id === this.draggingColumnId);
            if (sourceIndex < 0) {
                return;
            }

            nextIndex = Number.isFinite(Number(nextIndex)) ? Number(nextIndex) : sourceIndex;
            nextIndex = Math.max(0, Math.min(nextIndex, columns.length - 1));

            if (sourceIndex === nextIndex) {
                return;
            }

            const [column] = columns.splice(sourceIndex, 1);
            columns.splice(nextIndex, 0, column);
            this.draggingColumnMoved = true;
        },
        dropOnColumn(targetColumnId, event) {
            if (!this.activeBoardCanManage) {
                return;
            }

            if (this.draggingColumnId) {
                this.finishColumnDrag();
                return;
            }

            this.dropCard(targetColumnId);
        },
        dropOnCard(targetColumnId, targetCardIndex, event) {
            if (!this.activeBoardCanManage) {
                return;
            }

            if (this.draggingColumnId) {
                this.finishColumnDrag();
                return;
            }

            if (event && event.currentTarget && typeof event.currentTarget.getBoundingClientRect === 'function') {
                const rect = event.currentTarget.getBoundingClientRect();
                if (event.clientY > rect.top + (rect.height / 2)) {
                    targetCardIndex += 1;
                }
            }

            this.dropCard(targetColumnId, targetCardIndex);
        },
        dropOnColumnEnd(targetColumnId, event) {
            if (!this.activeBoardCanManage) {
                return;
            }

            if (this.draggingColumnId) {
                this.finishColumnDrag();
                return;
            }

            this.dropCard(targetColumnId);
        },
        startCardDrag(columnId, cardId) {
            if (!this.activeBoardCanManage || this.draggingColumnId) {
                return;
            }

            this.closeCardActionsMenu();
            this.draggingCard = {columnId, cardId};
            this.setBoardDraggingCursor();
        },
        endCardDrag() {
            this.draggingCard = null;
            this.clearBoardDraggingCursor();
        },
        dropCard(targetColumnId, targetCardIndex = null) {
            if (!this.activeBoardCanManage || !this.draggingCard) {
                return;
            }

            const sourceColumn = this.findColumn(this.draggingCard.columnId);
            const targetColumn = this.findColumn(targetColumnId);
            if (!sourceColumn || !targetColumn) {
                this.draggingCard = null;
                this.clearBoardDraggingCursor();
                return;
            }

            const sourceIndex = sourceColumn.cards.findIndex((card) => card.id === this.draggingCard.cardId);
            if (sourceIndex < 0) {
                this.draggingCard = null;
                this.clearBoardDraggingCursor();
                return;
            }

            const appendToEnd = targetCardIndex === null || targetCardIndex === undefined;
            const [card] = sourceColumn.cards.splice(sourceIndex, 1);
            let nextIndex = targetCardIndex;
            if (appendToEnd) {
                nextIndex = targetColumn.cards.length;
            }
            if (!appendToEnd && sourceColumn.id === targetColumn.id && sourceIndex < nextIndex) {
                nextIndex -= 1;
            }

            targetColumn.cards.splice(Math.max(0, nextIndex), 0, card);
            this.draggingCard = null;
            this.clearBoardDraggingCursor();
            this.persistBoardLayout();
        },
        setBoardDraggingCursor() {
            if (typeof document !== 'undefined' && document.body) {
                document.body.classList.add('board-dragging-cursor');
            }
        },
        clearBoardDraggingCursor() {
            if (typeof document !== 'undefined' && document.body) {
                document.body.classList.remove('board-dragging-cursor');
            }
        },
    },
    beforeDestroy() {
        this.clearBoardDraggingCursor();
    },
    metaInfo() {
        return {
            title: this.$i18n.t('Boards'),
        };
    },
};
</script>

<style scoped>
.board-scroll,
.board-column-body,
.board-card-dialog-scroll,
.board-dialog-checklist-scroll {
    scrollbar-width: thin;
}

.board-card-dialog-panel {
    display: flex;
    max-height: min(48rem, calc(100vh - 4rem));
    flex-direction: column;
}

.board-header-card {
    border-radius: 4px;
    --board-header-top-bg: var(--ui-surface-raised);
}

.board-header-toolbar {
    z-index: 80;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.board-header-top-row {
    background: var(--board-header-top-bg);
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.board-header-filter-cell,
.board-header-icon-cell {
    border-top-left-radius: 4px;
}

.board-header-icon-cell {
    display: inline-flex;
    width: 3.625rem;
    height: 100%;
    align-items: center;
    justify-content: center;
    color: var(--ui-text-heading);
    background: var(--board-header-top-bg);
    border: 0;
    outline: 0;
}

.board-header-create-button {
    border-radius: 0 4px 0 0;
}

.board-header-icon-cell:hover,
.board-header-icon-cell:focus,
.board-header-icon-cell:active {
    color: var(--ui-text-heading);
    background: var(--board-header-top-bg);
    outline: 0;
}

.board-select-cell {
    min-width: 18rem;
}

.board-select-trigger {
    width: 100%;
    background: var(--board-header-top-bg);
}

.board-select-menu {
    z-index: 120;
}

.board-filter-menu {
    z-index: 130;
}

.board-scroll::-webkit-scrollbar,
.board-column-body::-webkit-scrollbar,
.board-card-dialog-scroll::-webkit-scrollbar,
.board-dialog-checklist-scroll::-webkit-scrollbar {
    height: 8px;
    width: 8px;
}

.board-scroll::-webkit-scrollbar-thumb,
.board-column-body::-webkit-scrollbar-thumb,
.board-card-dialog-scroll::-webkit-scrollbar-thumb,
.board-dialog-checklist-scroll::-webkit-scrollbar-thumb {
    background: var(--ui-border-default);
    border-radius: 9999px;
}

.board-card-overdue-badge {
    color: #d15f5f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.board-card-completed-badge {
    display: inline-flex;
    width: 1rem;
    height: 1rem;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 9999px;
    color: #16a34a;
    border: 1px solid currentColor;
}

.board-column-completed-footer {
    --board-column-completed-bg: var(--board-header-top-bg, var(--ui-surface-raised));
    background: var(--board-column-completed-bg);
    border-top-color: var(--ui-border-subtle);
    border-bottom-right-radius: inherit;
    flex: 0 0 auto;
    min-height: 0;
}

.board-column-completed-footer-open {
    display: flex;
    flex: 0 1 auto;
    flex-direction: column;
    max-height: 50%;
}

.board-column-completed-scroll {
    background: var(--board-column-completed-bg, var(--ui-surface-raised));
    border-bottom-right-radius: inherit;
    display: flex;
    min-height: 0;
    flex-direction: column;
}

.board-column-completed-scroll-open {
    flex: 0 1 auto;
    max-height: 100%;
}

.board-column-completed-trigger {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--board-column-completed-bg, var(--ui-surface-raised));
    cursor: pointer;
}

.board-column-completed-trigger::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: -0.75rem;
    left: 0;
    height: 0.75rem;
    pointer-events: none;
    background: linear-gradient(to bottom, rgba(251, 251, 251, 0.92) 0%, rgba(251, 251, 251, 0) 100%);
}

.board-column-completed-trigger:hover,
.board-column-completed-trigger:focus,
.board-column-completed-trigger:active {
    background: var(--board-column-completed-bg, var(--ui-surface-raised));
}

.board-column-completed-panel {
    background: var(--board-column-completed-bg, var(--ui-surface-raised));
    position: relative;
}

.board-column-completed-panel .board-card-item:last-child {
    margin-bottom: 0;
}

.board-drag-surface {
    cursor: grab;
}

.board-drag-surface:active {
    cursor: grabbing;
}

.board-column-readonly .board-card-item,
.board-column-readonly button,
.board-column-readonly label,
.board-column-readonly input {
    cursor: default !important;
}

.board-color-preset-grid {
    position: relative;
    display: flex;
    flex-wrap: nowrap;
    gap: 0.5rem;
    align-items: center;
    width: 100%;
    padding: 0.75rem;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid var(--ui-border-default);
    border-radius: 0.25rem;
    scrollbar-width: none;
}

.board-color-preset-grid::-webkit-scrollbar {
    display: none;
}

.board-color-preset-grid::after {
    content: "";
    position: sticky;
    right: -0.75rem;
    align-self: stretch;
    width: 2rem;
    margin-left: -2rem;
    flex: 0 0 2rem;
    pointer-events: none;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 72%);
}

.board-color-preset {
    position: relative;
    aspect-ratio: 1 / 1;
    width: 1.5rem;
    flex: 0 0 1.5rem;
    padding: 0;
    appearance: none;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 9999px;
    background: transparent;
    box-shadow: none;
    outline: 0;
}

.board-color-preset:hover {
    box-shadow: none;
}

.board-color-preset[data-selected="true"] {
    border-color: var(--ui-border-strong);
}

.board-color-preset-swatch {
    display: block;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border: 0;
    border-radius: 9999px;
    pointer-events: none;
}

.board-color-preset[data-selected="true"] .board-color-preset-swatch {
    border: 3px solid #ffffff;
    background-clip: padding-box;
}

.board-color-preset-empty-swatch {
    border: 1px solid var(--ui-border-default);
    background:
        linear-gradient(135deg, transparent calc(50% - 0.5px), var(--ui-text-subtle) calc(50% - 0.5px), var(--ui-text-subtle) calc(50% + 0.5px), transparent calc(50% + 0.5px)),
        #ffffff;
}

:global(.board-dragging-cursor),
:global(.board-dragging-cursor *) {
    cursor: grabbing !important;
}

.board-checklist-text-input {
    box-sizing: border-box;
    line-height: 2.5rem !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

.board-card-checklist-label {
    margin-bottom: 0.25rem;
}

.board-card-attachment-list :deep(.stage-attachment) {
    width: 100%;
    align-self: stretch;
}
</style>
